/* eslint-disable react/prop-types */
/**
 * ConformIMF - Module 3 : Contrôle & Audit
 */
import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as CJSTooltip,
  Legend as CJSLegend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title as CJSTitle,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  CJSTooltip,
  CJSLegend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  CJSTitle
);

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { downloadCSV } from "../../utils/exportCSV";
import AuthContext from "../../context/AuthContext";
import { fadeInDown, fadeInUp } from "../../utils/animations";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const TAB_LABELS = [
  { label: "Référentiel", icon: "fact_check" },
  { label: "Plan annuel", icon: "event_note" },
  { label: "Missions", icon: "assignment" },
  { label: "Rapport de contrôle", icon: "description" },
  { label: "Tableau de bord", icon: "bar_chart" },
];

const MOIS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const NATURE_LABELS = {
  INTERNE: "Interne",
  CAC: "Commissaire aux comptes",
  CIF: "Contrôle interne (CIF)",
  COMMISSION_BANCAIRE: "Commission bancaire",
  BCEAO: "BCEAO",
  ANSFD: "ANSFD",
  DAI: "Direction de l'audit interne",
  AUTRE: "Autre",
};

const STATUT_COLORS = {
  NON_DEMARRE: { bg: "#f5f5f5", color: "#757575", label: "Non démarré", icon: "schedule" },
  EN_COURS: { bg: "#fff3e0", color: "#e65100", label: "En cours", icon: "hourglass_top" },
  REALISE: { bg: "#e8f5e9", color: "#2e7d32", label: "Réalisé", icon: "check_circle" },
  EN_RETARD: { bg: "#ffebee", color: "#c62828", label: "En retard", icon: "warning" },
};

const POIDS_COLOR = { 1: "#757575", 2: "#e65100", 3: "#c62828" };

const STATUT_MISSION_COLORS = {
  PLANIFIEE: { bg: "#f5f5f5", color: "#757575", label: "Planifiée", icon: "schedule" },
  EN_COURS: { bg: "#fff3e0", color: "#e65100", label: "En cours", icon: "hourglass_top" },
  CLOTUREE: { bg: "#e8f5e9", color: "#2e7d32", label: "Clôturée", icon: "check_circle" },
};

// Feuille de travail (§ 6.4, § 8.1). Mêmes barèmes que le backend, pour un
// affichage temps réel du risque avant même l'enregistrement (§ 9.5).
const RESULTAT_OPTIONS = [
  { value: "PAS_ANOMALIE", label: "Pas d'anomalies", color: "success" },
  { value: "ANOMALIE_MINEURE", label: "Anomalies mineures", color: "warning" },
  { value: "ANOMALIE_MAJEURE", label: "Anomalies majeures", color: "error" },
];

const RISQUE_PAR_RESULTAT = {
  PAS_ANOMALIE: "FAIBLE",
  ANOMALIE_MINEURE: "MOYEN",
  ANOMALIE_MAJEURE: "ELEVE",
};

const VALEUR_PAR_RESULTAT = { PAS_ANOMALIE: 1, ANOMALIE_MINEURE: 0.5, ANOMALIE_MAJEURE: 0 };

// Conformité d'une exigence, dérivée du résultat saisi (même principe que
// RISQUE_PAR_RESULTAT/VALEUR_PAR_RESULTAT ci-dessus). Affichée dans la grille
// d'évaluation à côté du niveau de risque de l'exigence.
const CONFORMITE_PAR_RESULTAT = {
  PAS_ANOMALIE: "CONFORME",
  ANOMALIE_MINEURE: "PARTIELLEMENT_CONFORME",
  ANOMALIE_MAJEURE: "NON_CONFORME",
};

// Prévisualisation client du taux/niveau de conformité et du niveau de risque
// du volet (§ 8.2-8.3), même formule que le serveur, à titre indicatif avant
// clôture. Le serveur reste la seule source de vérité au moment d'enregistrer.
function computeVoletPreview(resultats) {
  if (!resultats.length || resultats.some((r) => !r.resultat)) return null;
  const scoreObtenu = resultats.reduce(
    (sum, r) => sum + VALEUR_PAR_RESULTAT[r.resultat] * r.point_poids,
    0
  );
  const scoreMaximum = resultats.reduce((sum, r) => sum + r.point_poids, 0);
  const taux = scoreMaximum ? (scoreObtenu / scoreMaximum) * 100 : 0;

  let niveauConformite;
  if (taux >= 80) niveauConformite = "CONFORME";
  else if (taux >= 50) niveauConformite = "PARTIELLEMENT_CONFORME";
  else niveauConformite = "NON_CONFORME";

  const critiqueMajeur = resultats.some(
    (r) => r.point_poids === 3 && r.resultat === "ANOMALIE_MAJEURE"
  );
  let niveauRisque;
  if (critiqueMajeur || taux < 50) niveauRisque = "ELEVE";
  else if (taux >= 80) niveauRisque = "FAIBLE";
  else niveauRisque = "MOYEN";

  return { taux: Math.round(taux * 100) / 100, niveauConformite, niveauRisque };
}

// Brouillon du commentaire de conclusion, assemblé à partir des constats et
// recommandations déjà saisis sur les points en anomalie. Le contrôleur le
// corrige et le complète avant de valider la clôture.
function genererDraftCloture(resultats) {
  const anomalies = resultats.filter(
    (r) => r.resultat === "ANOMALIE_MINEURE" || r.resultat === "ANOMALIE_MAJEURE"
  );
  if (anomalies.length === 0) return "Aucune anomalie relevée sur ce volet.";
  return anomalies
    .map((r) => {
      const constat = r.constats?.trim();
      const reco = r.recommandation?.trim();
      let line = `${r.point_code} - ${r.point_exigence}`;
      if (constat) line += `\nConstat : ${constat}`;
      if (reco) line += `\nRecommandation : ${reco}`;
      return line;
    })
    .join("\n\n");
}

const NIVEAU_CONFORMITE_COLORS = {
  CONFORME: { bg: "#e8f5e9", color: "#2e7d32", label: "Conforme" },
  PARTIELLEMENT_CONFORME: { bg: "#fff3e0", color: "#e65100", label: "Partiellement conforme" },
  NON_CONFORME: { bg: "#ffebee", color: "#c62828", label: "Non conforme" },
};

const NIVEAU_RISQUE_COLORS = {
  FAIBLE: { bg: "#e8f5e9", color: "#2e7d32", label: "Faible" },
  MOYEN: { bg: "#fff3e0", color: "#e65100", label: "Moyen" },
  ELEVE: { bg: "#ffebee", color: "#c62828", label: "Élevé" },
};

const PREUVE_ICON = { PDF: "picture_as_pdf", IMAGE: "image", EXCEL: "table_chart" };

// Lacunes de conformité (§ 4.8, § 8.5)
const CAUSE_OPTIONS = [
  "Procédure inexistante",
  "Procédure inadaptée",
  "Non-application",
  "Insuffisance de formation",
  "Défaillance du système",
  "Autres",
];

const CONSEQUENCE_OPTIONS = [
  "Risque réglementaire",
  "Risque de blanchiment",
  "Risque financier",
  "Risque opérationnel",
  "Risque réputationnel",
];

const PRIORITE_LACUNE_COLORS = {
  Haute: { bg: "#ffebee", color: "#c62828" },
  Moyenne: { bg: "#fff3e0", color: "#e65100" },
  Faible: { bg: "#f5f5f5", color: "#757575" },
};

function StatutMissionChip({ statut }) {
  const cfg = STATUT_MISSION_COLORS[statut] || STATUT_MISSION_COLORS.PLANIFIEE;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: "0.85rem !important" }}>{cfg.icon}</Icon>}
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "0.7rem" }}
    />
  );
}

function StatutPlanChip({ statut }) {
  const cfg = STATUT_COLORS[statut] || STATUT_COLORS.NON_DEMARRE;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: "0.85rem !important" }}>{cfg.icon}</Icon>}
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "0.7rem" }}
    />
  );
}

function PlaceholderTab({ label }) {
  return (
    <MDBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
      gap={2}
      sx={{ background: "#f8f9fa", borderRadius: "12px", border: "1px dashed #c8d8ee" }}
    >
      <Icon sx={{ fontSize: "3rem !important", color: "#b0c4de" }}>hourglass_empty</Icon>
      <MDTypography variant="h6" sx={{ color: "#4B5E80", fontWeight: 600 }}>
        {label}
      </MDTypography>
      <MDTypography variant="body2" sx={{ color: "#9e9e9e", textAlign: "center", maxWidth: 360 }}>
        Cette rubrique sera disponible prochainement, selon le planning de développement du Module
        3.
      </MDTypography>
    </MDBox>
  );
}

// Éditeur de texte enrichi minimal (gras, italique, souligné, listes) pour le
// rapport de contrôle. Pas de dépendance externe : s'appuie sur
// `document.execCommand`, suffisant pour une mise en forme basique dans un
// outil interne. Le parent doit passer une `key` unique (ex. id du volet)
// pour forcer un remount lors du changement de contenu initial.
function RichTextEditor({ value, onChange, disabled }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
  }, []);

  function handleInput() {
    if (onChange) onChange(ref.current?.innerHTML || "");
  }

  function exec(command) {
    if (disabled) return;
    document.execCommand(command);
    handleInput();
  }

  const TOOLBAR = [
    { command: "bold", icon: "format_bold", label: "Gras" },
    { command: "italic", icon: "format_italic", label: "Italique" },
    { command: "underline", icon: "format_underlined", label: "Souligné" },
    { command: "insertUnorderedList", icon: "format_list_bulleted", label: "Liste à puces" },
    { command: "insertOrderedList", icon: "format_list_numbered", label: "Liste numérotée" },
  ];

  return (
    <MDBox sx={{ border: "1px solid #c8d0dc", borderRadius: "8px", overflow: "hidden" }}>
      {!disabled && (
        <MDBox
          display="flex"
          gap={0.5}
          sx={{ p: 0.5, borderBottom: "1px solid #e0e0e0", background: "#fafbfc" }}
        >
          {TOOLBAR.map((t) => (
            <Tooltip key={t.command} title={t.label}>
              <IconButton
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec(t.command)}
                sx={{ color: "#0A3475" }}
              >
                <Icon sx={{ fontSize: "1.1rem" }}>{t.icon}</Icon>
              </IconButton>
            </Tooltip>
          ))}
        </MDBox>
      )}
      <MDBox
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        sx={{
          p: 1.5,
          minHeight: 160,
          fontSize: "0.875rem",
          outline: "none",
          background: disabled ? "#f8f9fa" : "#fff",
          "& ul, & ol": { pl: 3 },
        }}
      />
    </MDBox>
  );
}

// Échappement HTML minimal, réutilisé par tous les exports imprimables.
const escHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Ouvre une fenêtre d'impression avec le HTML fourni. Même mécanisme que les
// rapports imprimables des Modules 1 et 2 (pas de génération PDF serveur).
function openPrintWindow(html, onBlocked) {
  const win = window.open("", "_blank", "width=1100,height=850");
  if (!win) {
    if (onBlocked) onBlocked();
    return;
  }
  win.document.write(html);
  win.document.close();
}

// Une tuile du bandeau d'indicateurs clés en tête des rapports imprimables
// (même convention que les rapports du Module 2, ex. conformite.js).
function kpiTile(value, label, warn = false) {
  return `<div class="kpi${warn ? " warn" : ""}"><div class="v">${escHtml(
    String(value)
  )}</div><div class="l">${escHtml(label)}</div></div>`;
}

// Habillage HTML commun (en-tête dégradé, bouton Imprimer, pied de page) pour
// tous les exports imprimables du module Contrôle & Audit.
function buildPrintShell({
  title,
  eyebrow,
  heading,
  subtitle,
  bodyHtml,
  kpiHtml = "",
  landscape = false,
}) {
  const today = new Date().toLocaleDateString("fr-FR");
  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>${escHtml(title)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:11px;color:#333;background:#fff;line-height:1.5}
  .no-print{position:fixed;top:14px;right:18px;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.85}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8;flex-wrap:wrap}
  .kpi{flex:1;min-width:80px;padding:14px 12px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:20px;font-weight:700;color:#0A3475}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.warn .v{color:#c62828}
  .chart-box{border:1px solid #eef0f5;border-radius:8px;padding:14px;background:#fafbff;margin-bottom:14px;max-width:420px}
  .chart-box img{width:100%;height:auto;display:block}
  .chart-title{font-size:11px;font-weight:700;color:#0A3475;margin-bottom:8px}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:22px 0 10px}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th{background:#1a237e;color:#fff;padding:6px 8px;text-align:left;
    font-size:9px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:6px 8px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:hover td{background:#f8faff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  @media print{
    .no-print{display:none}
    body{font-size:10px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,h2,th{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    h2{page-break-after:avoid}
    tr{page-break-inside:avoid}
  }
  @page{size:A4 ${landscape ? "landscape" : "portrait"};margin:12mm 10mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>
<div class="hdr">
  <div class="hdr-eye">${escHtml(eyebrow)}</div>
  <h1>${escHtml(heading)}</h1>
  <div class="hdr-sub">${subtitle}</div>
  <div class="hdr-date">Généré le ${today}</div>
</div>
${kpiHtml ? `<div class="kpi-strip">${kpiHtml}</div>` : ""}
<div class="content">${bodyHtml}</div>
<div class="ftr"><span>Document confidentiel - ConformIMF</span><strong>${today}</strong></div>
</body>
</html>`;
}

// Rapport de contrôle imprimable au format du rapport d'inspection de
// référence (outils/Rap_LBC_COTONOU_2024) : page de garde, table des matières,
// introduction, présentation de l'institution, rapport détaillé par volet,
// synthèse des lacunes et recommandations (N° | Lacunes | Risques |
// Recommandations | Échéancier | Responsable de suivi, regroupée par volet),
// conclusion et attestation. Mise en page sobre (noir et blanc, Arial Narrow)
// comme le modèle, distincte de l'habillage des autres exports (buildPrintShell).
const ROMAINS = ["I", "II", "III", "IV", "V"];
const htmlEstVide = (html) =>
  !String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

function buildRapportControleHtml(r, sections, voletsHtml, logoDataUrl = null) {
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : null);
  const moisRapport = new Date()
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    .toUpperCase();
  const themes = r.volets.map((v) => v.volet_theme).filter(Boolean);
  const periode =
    r.date_debut && r.date_fin
      ? `Du ${fmt(r.date_debut)} au ${fmt(r.date_fin)}`
      : r.date_debut
      ? `À compter du ${fmt(r.date_debut)}`
      : `Exercice ${r.annee_intervention}`;

  const avecPresentation = !htmlEstVide(sections.rapport_presentation_html);
  const numDetail = avecPresentation ? ROMAINS[1] : ROMAINS[0];
  const prefixe = avecPresentation ? "2" : "1";
  const numSynthese = `${prefixe}.${r.volets.length + 1}`;

  // ── Table des matières ──
  const toc = [
    `<li class="t1">INTRODUCTION</li>`,
    avecPresentation ? `<li class="t1">${ROMAINS[0]}. PRÉSENTATION DE L'INSTITUTION</li>` : "",
    `<li class="t1">${numDetail}. RAPPORT DÉTAILLÉ</li>`,
    ...r.volets.map(
      (v, i) => `<li class="t2">${prefixe}.${i + 1}. ${escHtml(v.volet_theme || v.volet_code)}</li>`
    ),
    `<li class="t2">${numSynthese}. Synthèse des lacunes et recommandations</li>`,
    `<li class="t1">CONCLUSION</li>`,
  ].join("");

  // ── Rapport détaillé ──
  const detail = r.volets
    .map((v, i) => {
      const titre = `<h2>${prefixe}.${i + 1}. ${escHtml(v.volet_theme || v.volet_code)}</h2>`;
      if (!v.date_cloture) {
        return `${titre}<p class="na">Volet non clôturé - conclusion non disponible.</p>`;
      }
      const niveau =
        NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.label || v.niveau_conformite || "-";
      const risque = NIVEAU_RISQUE_COLORS[v.niveau_risque]?.label || v.niveau_risque || "-";
      return `${titre}
        <p class="eval">Niveau de conformité : <strong>${escHtml(niveau)}</strong>${
        v.taux_conformite != null ? ` (${v.taux_conformite}%)` : ""
      } &nbsp;-&nbsp; Niveau de risque : <strong>${escHtml(risque)}</strong></p>
        <div class="txt">${voletsHtml[v.mission_volet_id] || ""}</div>`;
    })
    .join("");

  // ── Synthèse des lacunes et recommandations (regroupée par volet) ──
  const lacunes = r.lacunes || [];
  const ordreVolet = new Map(r.volets.map((v, i) => [v.mission_volet_id, i]));
  const groupes = [];
  lacunes
    .slice()
    .sort(
      (a, b) =>
        (ordreVolet.get(a.mission_volet_id) ?? 99) - (ordreVolet.get(b.mission_volet_id) ?? 99) ||
        String(a.numero_ordre).localeCompare(String(b.numero_ordre))
    )
    .forEach((l) => {
      const dernier = groupes[groupes.length - 1];
      if (dernier && dernier.id === l.mission_volet_id) dernier.items.push(l);
      else groupes.push({ id: l.mission_volet_id, theme: l.volet_theme, items: [l] });
    });
  let n = 0;
  const lignes = groupes
    .map(
      (g) =>
        `<tr class="grp"><td colspan="6">${escHtml(g.theme || "")}</td></tr>` +
        g.items
          .map((l) => {
            n += 1;
            const risques = l.consequences?.length
              ? l.consequences.join(" ; ")
              : `Risque ${(NIVEAU_RISQUE_COLORS[l.risque]?.label || l.risque || "").toLowerCase()}`;
            return `<tr>
              <td class="c">${n}</td>
              <td>${escHtml(l.constat || l.critere).replace(/\n/g, "<br>")}</td>
              <td>${escHtml(risques)}</td>
              <td>${escHtml(l.recommandation || "-").replace(/\n/g, "<br>")}</td>
              <td class="c">${escHtml(l.delai || "-")}</td>
              <td class="c">${escHtml(l.responsable || "-")}</td>
            </tr>`;
          })
          .join("")
    )
    .join("");
  const synthese = lacunes.length
    ? `<p>Au terme des travaux et au regard des lacunes relevées, la mission formule à l'endroit
        des organes d'administration, de gestion et de contrôle les recommandations suivantes :</p>
       <table>
         <thead><tr>
           <th style="width:4%">N°</th><th style="width:30%">Lacunes</th>
           <th style="width:13%">Risques</th><th style="width:29%">Recommandations</th>
           <th style="width:11%">Échéancier</th><th style="width:13%">Responsable de suivi</th>
         </tr></thead>
         <tbody>${lignes}</tbody>
       </table>`
    : `<p>La mission n'a relevé aucune lacune.</p>`;

  // ── Attestation ──
  const visa =
    sections.rapport_visa_titre || sections.rapport_visa_nom
      ? `<div class="sig"><div class="vu">VU</div><div>${
          sections.rapport_visa_titre ? `${escHtml(sections.rapport_visa_titre)},` : ""
        }</div><div class="nom">${escHtml(sections.rapport_visa_nom || "")}</div></div>`
      : "";

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Rapport de contrôle - ${escHtml(r.reference)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:"Arial Narrow",Arial,sans-serif;font-size:12pt;color:#000;background:#fff;line-height:1.45}
  .no-print{position:fixed;top:14px;right:18px;z-index:999;background:rgba(255,255,255,.95);
    padding:8px 10px;border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .page{max-width:820px;margin:0 auto;padding:40px 48px}
  .cover{min-height:1000px;display:flex;flex-direction:column;text-align:center;
    page-break-after:always}
  .cover .logo{max-height:95px;max-width:240px;margin:0 auto 10px;display:block}
  .cover .inst{font-size:15pt;font-weight:700;text-transform:uppercase}
  .cover .sep{margin:6px 0 4px;letter-spacing:4px}
  .cover .dir{font-size:12pt;font-weight:700;text-transform:uppercase}
  .cover .bloc{margin:auto 0;border-top:2px solid #000;border-bottom:2px solid #000;padding:28px 10px}
  .cover .titre{font-size:22pt;font-weight:700;letter-spacing:1px}
  .cover .objet{font-size:14pt;font-weight:700;text-transform:uppercase;margin-top:10px}
  .cover .ref{font-size:13pt;margin-top:10px}
  .cover .per{font-size:13pt;font-weight:700;margin-top:18px}
  .cover .mois{font-size:13pt;font-weight:700;margin-top:auto}
  .toc{page-break-after:always}
  .toc ul{list-style:none;margin-top:16px}
  .toc li{padding:5px 0;border-bottom:1px dotted #999}
  .toc .t1{font-weight:700}
  .toc .t2{padding-left:28px}
  h1{font-size:13pt;font-weight:700;text-transform:uppercase;margin:26px 0 10px;
    border-bottom:1.5px solid #000;padding-bottom:3px;page-break-after:avoid}
  h1.ctr{text-align:center;border:none}
  h2{font-size:12pt;font-weight:700;margin:18px 0 6px;page-break-after:avoid}
  p{margin:0 0 8px;text-align:justify}
  ul,ol{margin:0 0 8px 28px}
  li{margin-bottom:3px}
  .txt p{text-align:justify}
  .eval{font-style:italic;font-size:11pt}
  .na{font-style:italic;color:#C00000}
  table{width:100%;border-collapse:collapse;font-size:10pt;margin-top:6px}
  th,td{border:1px solid #000;padding:5px 6px;vertical-align:top;text-align:left}
  th{font-weight:700;text-align:center;background:#d9d9d9}
  td.c{text-align:center}
  tr.grp td{font-weight:700;background:#f2f2f2}
  .attest{margin-top:36px;page-break-inside:avoid}
  .attest .lbl{font-weight:700;text-decoration:underline;margin-bottom:18px}
  .sigs{display:flex;justify-content:space-between;gap:40px}
  .sig{text-align:center;min-width:220px}
  .sig .nom{margin-top:56px;font-weight:700}
  .sig .vu{font-weight:700;margin-bottom:4px}
  .valide{margin-top:18px;font-size:9pt;font-style:italic}
  @media print{
    .no-print{display:none}
    .page{padding:0;max-width:none}
    .cover{min-height:255mm}
    th,tr.grp td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
  }
  @page{size:A4 portrait;margin:18mm 16mm;
    @bottom-center{content:counter(page);font-family:Arial,sans-serif;font-size:9pt}}
  @page :first{@bottom-center{content:none}}
</style>
</head>
<body>
<div class="no-print"><button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button></div>
<div class="page">
  <section class="cover">
    ${logoDataUrl ? `<img class="logo" src="${logoDataUrl}" alt="">` : ""}
    <div class="inst">${escHtml(r.institution_nom || "")}</div>
    <div class="sep">----------------------</div>
    <div class="dir">${escHtml(r.rapport_direction || "Contrôle & Audit")}</div>
    <div class="bloc">
      <div class="titre">RAPPORT DE CONTRÔLE</div>
      ${themes.length ? `<div class="objet">sur ${escHtml(themes.join(" / "))}</div>` : ""}
      <div class="ref">Mission ${escHtml(r.reference)}</div>
      <div class="per">${escHtml(periode)}</div>
    </div>
    <div class="mois">${escHtml(moisRapport)}</div>
  </section>

  <section class="toc">
    <h1 class="ctr">Table des matières</h1>
    <ul>${toc}</ul>
  </section>

  <h1>Introduction</h1>
  <div class="txt">${sections.rapport_introduction_html || ""}</div>

  ${
    avecPresentation
      ? `<h1>${ROMAINS[0]}. Présentation de l'institution</h1>
         <div class="txt">${sections.rapport_presentation_html}</div>`
      : ""
  }

  <h1>${numDetail}. Rapport détaillé</h1>
  ${detail}
  <h2>${numSynthese}. Synthèse des lacunes et recommandations</h2>
  ${synthese}

  <h1>Conclusion</h1>
  <div class="txt">${sections.rapport_conclusion_html || ""}</div>

  <div class="attest">
    <div class="lbl">ATTESTATION DU RAPPORT DE CONTRÔLE :</div>
    <div class="sigs">
      <div class="sig"><div class="vu">&nbsp;</div><div>Le Chef de mission,</div><div class="nom">${escHtml(
        r.chef_mission_nom || ""
      )}</div></div>
      ${visa}
    </div>
    ${
      r.rapport_statut === "VALIDE" && r.rapport_valide_le
        ? `<p class="valide">Rapport validé le ${escHtml(fmt(r.rapport_valide_le))}${
            r.rapport_valide_par_nom ? ` par ${escHtml(r.rapport_valide_par_nom)}` : ""
          }.</p>`
        : ""
    }
  </div>
</div>
</body>
</html>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function ControleAudit() {
  const { user } = useContext(AuthContext);
  const userRole = user?.profile?.role;
  const userAllRoles = useMemo(
    () => user?.profile?.all_roles || (userRole ? [userRole] : []),
    [user, userRole]
  );
  const hasAnyRole = useCallback(
    (roles) => roles.some((r) => userAllRoles.includes(r)),
    [userAllRoles]
  );
  const isAdminGlobal = userRole === "ADMIN_GLOBAL";
  const canParametrerReferentiel = isAdminGlobal;
  const canCreatePlanLine = hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE"]);
  // Exécution de la feuille de travail (saisie des résultats, upload de
  // preuves, clôture de volet). Mêmes rôles que RESULTAT_WRITE_ROLES côté
  // serveur ; le CONTROLEUR est filtré à ses propres missions par l'API.
  const canEditResultats = hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE", "CONTROLEUR"]);
  const isControleurOnly =
    hasAnyRole(["CONTROLEUR"]) &&
    !hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE", "ADMIN_GLOBAL"]);

  const canEditPlanLine = useCallback(
    (plan) => {
      if (canCreatePlanLine) return true;
      if (isControleurOnly) return plan.ressource_assignee_id === user?.profile?.id;
      return false;
    },
    [canCreatePlanLine, isControleurOnly, user]
  );

  // ── État global ─────────────────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = parseInt(searchParams.get("tab"), 10);
    if (!hasAnyRole(["ADMIN_GLOBAL", "ADMIN_INSTITUTION", "GESTIONNAIRE", "CONTROLEUR"])) return 4;
    return isNaN(t) || t < 0 || t >= TAB_LABELS.length ? 0 : t;
  });
  const canSeeMissionsTabs = hasAnyRole([
    "ADMIN_GLOBAL",
    "ADMIN_INSTITUTION",
    "GESTIONNAIRE",
    "CONTROLEUR",
  ]);
  // Profil "Direction Générale" (§ 6.6/§ 7 du cahier) : le rôle UTILISATEUR
  // voit en plus l'onglet Rapport de contrôle (lecture seule), en plus du
  // tableau de bord - mais pas Référentiel/Plan annuel/Missions.
  const canSeeRapportTab = canSeeMissionsTabs || hasAnyRole(["UTILISATEUR"]);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });

  useEffect(() => {
    if (isAdminGlobal) {
      api
        .get("/api/institutions/")
        .then((r) => setInstitutions(r.data))
        .catch(() => {});
    }
  }, [isAdminGlobal]);

  const institutionParam = isAdminGlobal ? selectedInstitution || undefined : undefined;

  // ── Onglet A : Référentiel ────────────────────────────────────────────────
  const [volets, setVolets] = useState([]);
  const [loadingVolets, setLoadingVolets] = useState(false);
  const [expandedVolets, setExpandedVolets] = useState(new Set());
  const [pointsByVolet, setPointsByVolet] = useState({});
  const [domaines, setDomaines] = useState([]);
  const [filterReferentiel, setFilterReferentiel] = useState("");

  useEffect(() => {
    api
      .get("/api/domaines/")
      .then((r) => setDomaines(r.data))
      .catch(() => {});
  }, []);

  const domaineNom = useCallback((id) => domaines.find((d) => d.id === id)?.nom, [domaines]);

  const loadVolets = useCallback(() => {
    setLoadingVolets(true);
    const params = new URLSearchParams();
    if (canParametrerReferentiel) params.append("include_inactive", "true");
    api
      .get(`/api/controle/volets?${params}`)
      .then((r) => setVolets(r.data))
      .catch(() => showSnack("Erreur lors du chargement du référentiel.", "error"))
      .finally(() => setLoadingVolets(false));
  }, [canParametrerReferentiel, showSnack]);

  useEffect(() => {
    if (activeTab === 0) loadVolets();
  }, [activeTab, loadVolets]);

  useEffect(() => {
    // Le référentiel est aussi nécessaire à l'onglet Missions (sélection des
    // volets à couvrir), même si l'onglet Référentiel n'a pas été visité.
    if (activeTab === 2 && volets.length === 0) loadVolets();
  }, [activeTab, volets.length, loadVolets]);

  const toggleVolet = (voletId) => {
    setExpandedVolets((prev) => {
      const next = new Set(prev);
      if (next.has(voletId)) {
        next.delete(voletId);
      } else {
        next.add(voletId);
        if (pointsByVolet[voletId] === undefined) {
          const params = canParametrerReferentiel ? "?include_inactive=true" : "";
          api
            .get(`/api/controle/volets/${voletId}/points-controle${params}`)
            .then((r) => setPointsByVolet((prevPts) => ({ ...prevPts, [voletId]: r.data })))
            .catch(() => showSnack("Erreur lors du chargement des points de contrôle.", "error"));
        }
      }
      return next;
    });
  };

  const [dialogVolet, setDialogVolet] = useState(false);
  const [editingVolet, setEditingVolet] = useState(null);
  const [formVolet, setFormVolet] = useState({
    code: "",
    theme: "",
    objectif: "",
    resultat_attendu: "",
    domaine_id: "",
    ordre: 0,
    actif: true,
  });
  const [savingVolet, setSavingVolet] = useState(false);

  function openCreateVolet() {
    setEditingVolet(null);
    setFormVolet({
      code: "",
      theme: "",
      objectif: "",
      resultat_attendu: "",
      domaine_id: "",
      ordre: volets.length,
      actif: true,
    });
    setDialogVolet(true);
  }

  function openEditVolet(v) {
    setEditingVolet(v);
    setFormVolet({
      code: v.code,
      theme: v.theme,
      objectif: v.objectif || "",
      resultat_attendu: v.resultat_attendu || "",
      domaine_id: v.domaine_id || "",
      ordre: v.ordre,
      actif: v.actif,
    });
    setDialogVolet(true);
  }

  async function saveVolet() {
    if (!formVolet.code.trim() || !formVolet.theme.trim()) {
      showSnack("Le code et le thème sont obligatoires.", "warning");
      return;
    }
    const payload = {
      ...formVolet,
      domaine_id: formVolet.domaine_id || null,
      ordre: Number(formVolet.ordre) || 0,
    };
    setSavingVolet(true);
    try {
      if (editingVolet) {
        await api.put(`/api/controle/volets/${editingVolet.id}`, payload);
        showSnack("Volet mis à jour avec succès.");
      } else {
        await api.post("/api/controle/volets", payload);
        showSnack("Volet créé avec succès.");
      }
      setDialogVolet(false);
      loadVolets();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingVolet(false);
    }
  }

  const [dialogPoint, setDialogPoint] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [formPoint, setFormPoint] = useState({
    volet_id: null,
    code: "",
    exigence: "",
    poids: 1,
    source: "",
    point_controle: "",
    test_audit: "",
    preuve_attendue: "",
    ordre: 0,
    actif: true,
  });
  const [savingPoint, setSavingPoint] = useState(false);

  function openCreatePoint(voletId) {
    setEditingPoint(null);
    setFormPoint({
      volet_id: voletId,
      code: "",
      exigence: "",
      poids: 1,
      source: "",
      point_controle: "",
      test_audit: "",
      preuve_attendue: "",
      ordre: (pointsByVolet[voletId] || []).length,
      actif: true,
    });
    setDialogPoint(true);
  }

  function openEditPoint(p) {
    setEditingPoint(p);
    setFormPoint({
      volet_id: p.volet_id,
      code: p.code,
      exigence: p.exigence,
      poids: p.poids,
      source: p.source || "",
      point_controle: p.point_controle || "",
      test_audit: p.test_audit || "",
      preuve_attendue: p.preuve_attendue || "",
      ordre: p.ordre,
      actif: p.actif,
    });
    setDialogPoint(true);
  }

  function reloadPointsForVolet(voletId) {
    const params = canParametrerReferentiel ? "?include_inactive=true" : "";
    api
      .get(`/api/controle/volets/${voletId}/points-controle${params}`)
      .then((r) => setPointsByVolet((prev) => ({ ...prev, [voletId]: r.data })))
      .catch(() => {});
  }

  async function savePoint() {
    if (!formPoint.code.trim() || !formPoint.exigence.trim()) {
      showSnack("Le code et l'exigence sont obligatoires.", "warning");
      return;
    }
    const payload = {
      ...formPoint,
      ordre: Number(formPoint.ordre) || 0,
      poids: Number(formPoint.poids),
    };
    setSavingPoint(true);
    try {
      if (editingPoint) {
        await api.put(`/api/controle/points-controle/${editingPoint.id}`, payload);
        showSnack("Point de contrôle mis à jour avec succès.");
      } else {
        await api.post("/api/controle/points-controle", payload);
        showSnack("Point de contrôle créé avec succès.");
      }
      setDialogPoint(false);
      reloadPointsForVolet(formPoint.volet_id);
      loadVolets();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingPoint(false);
    }
  }

  function deactivatePoint(p) {
    openConfirm(`Désactiver le point de contrôle « ${p.code} » ?`, async () => {
      try {
        await api.delete(`/api/controle/points-controle/${p.id}`);
        showSnack("Point de contrôle désactivé avec succès.");
        reloadPointsForVolet(p.volet_id);
        loadVolets();
      } catch {
        showSnack("Erreur lors de la désactivation.", "error");
      }
    });
  }

  function renderPointsTable(volet) {
    const points = pointsByVolet[volet.id];
    if (points === undefined) {
      return (
        <MDBox display="flex" justifyContent="center" p={2}>
          <CircularProgress size={22} color="info" />
        </MDBox>
      );
    }
    const columns = [
      { Header: "Code", accessor: "code", width: "9%" },
      { Header: "Exigence", accessor: "exigence", width: "27%", wrap: true },
      {
        Header: "Poids",
        accessor: "poids",
        width: "7%",
        Cell: ({ row }) => (
          <Chip
            label={row.original.poids}
            size="small"
            sx={{
              bgcolor: `${POIDS_COLOR[row.original.poids]}22`,
              color: POIDS_COLOR[row.original.poids],
              fontWeight: 700,
            }}
          />
        ),
      },
      { Header: "Source", accessor: "source", width: "14%", wrap: true },
      { Header: "Point de contrôle", accessor: "point_controle", width: "19%", wrap: true },
      {
        Header: "Détails",
        accessor: "details",
        width: "8%",
        Cell: ({ row }) => {
          const p = row.original;
          if (!p.test_audit && !p.preuve_attendue) {
            return (
              <MDTypography variant="caption" sx={{ color: "#c0c0c0" }}>
                -
              </MDTypography>
            );
          }
          return (
            <Tooltip
              title={
                <MDBox sx={{ maxWidth: 280, py: 0.5 }}>
                  {p.test_audit && (
                    <MDBox mb={p.preuve_attendue ? 1 : 0}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#fff", display: "block" }}
                      >
                        Test d&apos;audit
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#e0e0e0" }}>
                        {p.test_audit}
                      </MDTypography>
                    </MDBox>
                  )}
                  {p.preuve_attendue && (
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#fff", display: "block" }}
                      >
                        Preuve attendue
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#e0e0e0" }}>
                        {p.preuve_attendue}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              }
            >
              <IconButton size="small" sx={{ color: "#186A81" }}>
                <Icon sx={{ fontSize: "1.1rem" }}>info</Icon>
              </IconButton>
            </Tooltip>
          );
        },
      },
      ...(canParametrerReferentiel
        ? [
            {
              Header: "Actions",
              accessor: "actions",
              width: "16%",
              Cell: ({ row }) => {
                const p = row.original;
                return (
                  <MDBox display="flex" gap={0.5}>
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={() => openEditPoint(p)}
                        sx={{ color: "#0A3475" }}
                      >
                        <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    {p.actif && (
                      <Tooltip title="Désactiver">
                        <IconButton
                          size="small"
                          onClick={() => deactivatePoint(p)}
                          sx={{ color: "#c62828" }}
                        >
                          <Icon sx={{ fontSize: "1rem" }}>block</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </MDBox>
                );
              },
            },
          ]
        : []),
    ];
    return (
      <MDBox px={2} pb={2}>
        {canParametrerReferentiel && (
          <MDBox display="flex" justifyContent="flex-end" mb={1}>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => openCreatePoint(volet.id)}
            >
              <Icon sx={{ mr: 0.5 }}>add</Icon> Point de contrôle
            </MDButton>
          </MDBox>
        )}
        {points.length === 0 ? (
          <MDTypography variant="caption" color="text">
            Aucun point de contrôle.
          </MDTypography>
        ) : (
          <DataTable
            table={{ columns, rows: points }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
          />
        )}
      </MDBox>
    );
  }

  const filteredVolets = useMemo(() => {
    const q = filterReferentiel.trim().toLowerCase();
    if (!q) return volets;
    return volets.filter(
      (v) => v.code.toLowerCase().includes(q) || v.theme.toLowerCase().includes(q)
    );
  }, [volets, filterReferentiel]);

  const totalPointsReferentiel = useMemo(
    () => volets.reduce((sum, v) => sum + (v.nb_points_controle || 0), 0),
    [volets]
  );

  function renderReferentielTab() {
    return (
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <MDBox>
            <MDTypography variant="body2" sx={{ color: "#5c6b7f", maxWidth: 560 }}>
              Volets et points de contrôle du dispositif LBC/FT/FP et des autres dispositions
              réglementaires applicables (référentiel commun à toutes les institutions).
            </MDTypography>
            <MDBox display="flex" gap={1} mt={1.5} flexWrap="wrap">
              <Chip
                size="small"
                variant="outlined"
                icon={<Icon sx={{ fontSize: "1rem !important" }}>folder_open</Icon>}
                label={`${volets.length} volet${volets.length > 1 ? "s" : ""}`}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<Icon sx={{ fontSize: "1rem !important" }}>fact_check</Icon>}
                label={`${totalPointsReferentiel} points de contrôle`}
              />
            </MDBox>
          </MDBox>
          {canParametrerReferentiel && (
            <MDButton variant="gradient" color="info" onClick={openCreateVolet}>
              <Icon sx={{ mr: 0.5 }}>add</Icon> Nouveau volet
            </MDButton>
          )}
        </MDBox>

        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
          pb={2}
          sx={{ borderBottom: "1px solid #eee" }}
        >
          <MDInput
            placeholder="Rechercher un volet (code ou thème)…"
            value={filterReferentiel}
            onChange={(e) => setFilterReferentiel(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon>search</Icon>
                </InputAdornment>
              ),
            }}
          />
          <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
              Poids :
            </MDTypography>
            {[1, 2, 3].map((p) => (
              <MDBox key={p} display="flex" alignItems="center" gap={0.5}>
                <Chip
                  label={p}
                  size="small"
                  sx={{
                    bgcolor: `${POIDS_COLOR[p]}22`,
                    color: POIDS_COLOR[p],
                    fontWeight: 700,
                    height: 20,
                  }}
                />
                <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                  {p === 1 ? "mineur" : p === 2 ? "moyen" : "critique"}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>

        {loadingVolets ? (
          <MDBox display="flex" justifyContent="center" p={5}>
            <CircularProgress color="info" />
          </MDBox>
        ) : filteredVolets.length === 0 ? (
          <Card>
            <MDBox p={4} textAlign="center">
              <Icon sx={{ fontSize: 48, color: "grey.400" }}>fact_check</Icon>
              <MDTypography color="text" mt={1} variant="h6">
                {volets.length === 0
                  ? "Aucun volet de contrôle."
                  : "Aucun volet ne correspond à la recherche."}
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          filteredVolets.map((v) => (
            <Accordion
              key={v.id}
              expanded={expandedVolets.has(v.id)}
              onChange={() => toggleVolet(v.id)}
              sx={{
                mb: 1,
                "&:before": { display: "none" },
                borderRadius: "8px !important",
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
                sx={{ background: "#f8f9fa" }}
              >
                <MDBox display="flex" alignItems="center" gap={1.5} width="100%" flexWrap="wrap">
                  <Chip
                    label={v.code}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: "#0A3475", color: "#fff" }}
                  />
                  <MDTypography variant="button" fontWeight="medium" sx={{ flexGrow: 1 }}>
                    {v.theme}
                  </MDTypography>
                  {domaineNom(v.domaine_id) && (
                    <Chip
                      label={domaineNom(v.domaine_id)}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: "#186A81", color: "#186A81" }}
                    />
                  )}
                  <Chip
                    label={`${v.nb_points_controle} point${v.nb_points_controle > 1 ? "s" : ""}`}
                    size="small"
                    variant="outlined"
                  />
                  {!v.actif && (
                    <Chip label="Inactif" size="small" sx={{ bgcolor: "#eee", color: "#888" }} />
                  )}
                  {canParametrerReferentiel && (
                    <Tooltip title="Modifier le volet">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditVolet(v);
                        }}
                        sx={{ color: "#0A3475" }}
                      >
                        <Icon sx={{ fontSize: "1.1rem" }}>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </MDBox>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {(v.objectif || v.resultat_attendu) && (
                  <MDBox
                    px={2}
                    pt={2}
                    pb={1}
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    sx={{ background: "#fafbfc" }}
                  >
                    {v.objectif && (
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            color: "#0A3475",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Objectif
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: "#5c6b7f", display: "block" }}>
                          {v.objectif}
                        </MDTypography>
                      </MDBox>
                    )}
                    {v.resultat_attendu && (
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            color: "#0A3475",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Résultat attendu
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: "#5c6b7f", display: "block" }}>
                          {v.resultat_attendu}
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )}
                {renderPointsTable(v)}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </MDBox>
    );
  }

  // ── Onglet B : Plan annuel ────────────────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [planAnnuel, setPlanAnnuel] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [utilisateursInstitution, setUtilisateursInstitution] = useState([]);

  const loadPlanAnnuel = useCallback(() => {
    setLoadingPlan(true);
    const params = new URLSearchParams();
    params.append("annee", selectedYear);
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/plan-annuel?${params}`)
      .then((r) => setPlanAnnuel(r.data))
      .catch(() => showSnack("Erreur lors du chargement du plan annuel.", "error"))
      .finally(() => setLoadingPlan(false));
  }, [selectedYear, institutionParam, showSnack]);

  useEffect(() => {
    if (activeTab === 1) loadPlanAnnuel();
  }, [activeTab, loadPlanAnnuel]);

  useEffect(() => {
    if (activeTab !== 1 && activeTab !== 2) return;
    const params = new URLSearchParams();
    if (institutionParam) params.append("institution_id", institutionParam);
    if (!isAdminGlobal || institutionParam) {
      api
        .get(`/api/controle/utilisateurs-institution?${params}`)
        .then((r) => setUtilisateursInstitution(r.data))
        .catch(() => {});
    }
  }, [activeTab, institutionParam, isAdminGlobal]);

  const indicateursPlan = useMemo(() => {
    const total = planAnnuel.length;
    const realisees = planAnnuel.filter((p) => p.statut === "REALISE").length;
    const enCours = planAnnuel.filter((p) => p.statut === "EN_COURS").length;
    const enRetard = planAnnuel.filter((p) => p.statut === "EN_RETARD").length;
    const taux = total > 0 ? Math.round((realisees / total) * 100) : 0;
    return { total, realisees, enCours, enRetard, taux };
  }, [planAnnuel]);

  const [dialogPlan, setDialogPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formPlan, setFormPlan] = useState({
    volet_id: "",
    perimetre: "",
    frequence: "",
    priorite: "",
    ressource_assignee_id: "",
    periode_prevue: "",
    duree_estimee_jours: "",
  });
  const [savingPlan, setSavingPlan] = useState(false);

  function openCreatePlan() {
    setEditingPlan(null);
    setFormPlan({
      volet_id: "",
      perimetre: "",
      frequence: "",
      priorite: "",
      ressource_assignee_id: "",
      periode_prevue: "",
      duree_estimee_jours: "",
    });
    setDialogPlan(true);
  }

  function openEditPlan(p) {
    setEditingPlan(p);
    setFormPlan({
      volet_id: p.volet_id,
      perimetre: p.perimetre || "",
      frequence: p.frequence || "",
      priorite: p.priorite || "",
      ressource_assignee_id: p.ressource_assignee_id || "",
      periode_prevue: p.periode_prevue || "",
      duree_estimee_jours: p.duree_estimee_jours || "",
    });
    setDialogPlan(true);
  }

  async function savePlan() {
    if (!editingPlan && !formPlan.volet_id) {
      showSnack("Le volet est obligatoire.", "warning");
      return;
    }
    const payload = {
      annee: selectedYear,
      volet_id: Number(formPlan.volet_id),
      perimetre: formPlan.perimetre || null,
      frequence: formPlan.frequence || null,
      priorite: formPlan.priorite || null,
      ressource_assignee_id: formPlan.ressource_assignee_id
        ? Number(formPlan.ressource_assignee_id)
        : null,
      periode_prevue: formPlan.periode_prevue || null,
      duree_estimee_jours: formPlan.duree_estimee_jours
        ? Number(formPlan.duree_estimee_jours)
        : null,
    };
    setSavingPlan(true);
    try {
      if (editingPlan) {
        // eslint-disable-next-line no-unused-vars
        const { annee, volet_id, ...updatePayload } = payload;
        await api.put(`/api/controle/plan-annuel/${editingPlan.id}`, updatePayload);
        showSnack("Ligne de plan annuel mise à jour avec succès.");
      } else {
        await api.post("/api/controle/plan-annuel", payload);
        showSnack("Ligne de plan annuel créée avec succès.");
      }
      setDialogPlan(false);
      loadPlanAnnuel();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingPlan(false);
    }
  }

  function deletePlan(p) {
    openConfirm("Supprimer cette ligne du plan annuel ?", async () => {
      try {
        await api.delete(`/api/controle/plan-annuel/${p.id}`);
        showSnack("Ligne de plan annuel supprimée avec succès.");
        loadPlanAnnuel();
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  const planColumns = useMemo(
    () => [
      {
        Header: "Volet",
        accessor: "volet_theme",
        width: "20%",
        wrap: true,
        Cell: ({ row }) => (
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" sx={{ display: "block" }}>
              {row.original.volet_code}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.original.volet_theme}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Périmètre",
        accessor: "perimetre",
        width: "15%",
        wrap: true,
        Cell: ({ row }) => row.original.perimetre || "-",
      },
      {
        Header: "Fréquence / Priorité",
        accessor: "frequence",
        width: "13%",
        Cell: ({ row }) => (
          <MDBox>
            <MDTypography variant="caption" sx={{ display: "block" }}>
              {row.original.frequence || "-"}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.original.priorite || "-"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Ressource",
        accessor: "ressource_assignee_nom",
        width: "14%",
        Cell: ({ row }) => row.original.ressource_assignee_nom || "-",
      },
      {
        Header: "Période / Statut",
        accessor: "periode_prevue",
        width: "16%",
        Cell: ({ row }) => (
          <MDBox>
            <MDTypography variant="caption" sx={{ display: "block" }}>
              {row.original.periode_prevue
                ? `${row.original.periode_prevue}${
                    row.original.duree_estimee_jours != null
                      ? ` (${row.original.duree_estimee_jours} j)`
                      : ""
                  }`
                : "-"}
            </MDTypography>
            <StatutPlanChip statut={row.original.statut} />
          </MDBox>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "9%",
        Cell: ({ row }) => {
          const p = row.original;
          if (!canEditPlanLine(p)) return null;
          return (
            <MDBox display="flex" gap={0.5}>
              <Tooltip title="Modifier">
                <IconButton size="small" onClick={() => openEditPlan(p)} sx={{ color: "#0A3475" }}>
                  <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                </IconButton>
              </Tooltip>
              {canCreatePlanLine && (
                <Tooltip title="Supprimer">
                  <IconButton size="small" onClick={() => deletePlan(p)} sx={{ color: "#c62828" }}>
                    <Icon sx={{ fontSize: "1rem" }}>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],
    [canEditPlanLine, canCreatePlanLine]
  );

  function renderIndicateursPlan() {
    const tiles = [
      {
        label: "Lignes planifiées",
        value: indicateursPlan.total,
        icon: "list_alt",
        color: "#0A3475",
      },
      {
        label: "Réalisées",
        value: indicateursPlan.realisees,
        icon: "check_circle",
        color: "#2e7d32",
      },
      {
        label: "En cours",
        value: indicateursPlan.enCours,
        icon: "hourglass_top",
        color: "#e65100",
      },
      { label: "En retard", value: indicateursPlan.enRetard, icon: "warning", color: "#c62828" },
      {
        label: "Taux de réalisation",
        value: `${indicateursPlan.taux}%`,
        icon: "insights",
        color: "#186A81",
      },
    ];
    return (
      <Grid container spacing={2} mb={3}>
        {tiles.map((t) => (
          <Grid item xs={6} md={2.4} key={t.label}>
            <Card sx={{ p: 2, textAlign: "center", height: "100%" }}>
              <Icon sx={{ color: t.color, fontSize: "1.6rem !important" }}>{t.icon}</Icon>
              <MDTypography variant="h4" sx={{ color: t.color, fontWeight: 700, mt: 0.5 }}>
                {t.value}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {t.label}
              </MDTypography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  function renderCalendrier() {
    return (
      <Grid container spacing={1.5} mt={0.5}>
        {MOIS.map((mois) => {
          const lignes = planAnnuel.filter((p) => p.periode_prevue === mois);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={mois}>
              <MDBox
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  p: 1.5,
                  minHeight: 110,
                  bgcolor: "#fafbfc",
                }}
              >
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ color: "#0A3475", textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  {mois}
                </MDTypography>
                <MDBox mt={1} display="flex" flexDirection="column" gap={0.5}>
                  {lignes.length === 0 ? (
                    <MDTypography variant="caption" sx={{ color: "#c0c0c0", fontStyle: "italic" }}>
                      -
                    </MDTypography>
                  ) : (
                    lignes.map((p) => {
                      const cfg = STATUT_COLORS[p.statut] || STATUT_COLORS.NON_DEMARRE;
                      return (
                        <Chip
                          key={p.id}
                          label={p.volet_code || `Volet #${p.volet_id}`}
                          size="small"
                          sx={{
                            bgcolor: cfg.bg,
                            color: cfg.color,
                            fontWeight: 600,
                            justifyContent: "flex-start",
                          }}
                        />
                      );
                    })
                  )}
                </MDBox>
              </MDBox>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  function renderPlanAnnuelTab() {
    return (
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Année</InputLabel>
            <Select
              label="Année"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ height: "45px" }}
            >
              {[selectedYear - 1, selectedYear, selectedYear + 1, selectedYear + 2]
                .filter((v, i, arr) => arr.indexOf(v) === i)
                .sort((a, b) => a - b)
                .map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <MDBox display="flex" gap={1}>
            <MDButton size="small" variant="outlined" color="success" onClick={exporterPlanExcel}>
              <Icon sx={{ mr: 0.5 }}>download</Icon> Excel
            </MDButton>
            <MDButton size="small" variant="outlined" color="info" onClick={imprimerPlan}>
              <Icon sx={{ mr: 0.5 }}>print</Icon> Imprimer
            </MDButton>
            {canCreatePlanLine && (
              <MDButton variant="gradient" color="info" onClick={openCreatePlan}>
                <Icon sx={{ mr: 0.5 }}>add</Icon> Nouvelle ligne de plan
              </MDButton>
            )}
          </MDBox>
        </MDBox>

        {renderIndicateursPlan()}

        {loadingPlan ? (
          <MDBox display="flex" justifyContent="center" p={5}>
            <CircularProgress color="info" />
          </MDBox>
        ) : planAnnuel.length === 0 ? (
          <Card>
            <MDBox p={4} textAlign="center">
              <Icon sx={{ fontSize: 48, color: "grey.400" }}>event_note</Icon>
              <MDTypography color="text" mt={1} variant="h6">
                Aucune ligne de plan pour {selectedYear}.
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <DataTable
            table={{ columns: planColumns, rows: planAnnuel }}
            isSorted
            entriesPerPage
            showTotalEntries
          />
        )}

        <MDTypography variant="h6" sx={{ mt: 4, mb: 1, color: "#0A3475" }}>
          Calendrier annuel
        </MDTypography>
        {renderCalendrier()}
      </MDBox>
    );
  }

  function imprimerPlan() {
    if (planAnnuel.length === 0) {
      showSnack("Aucune ligne de plan à imprimer.", "warning");
      return;
    }
    const rows = planAnnuel
      .map(
        (p) => `<tr>
      <td><strong>${escHtml(p.volet_code)}</strong><br>${escHtml(p.volet_theme)}</td>
      <td>${escHtml(p.perimetre) || "-"}</td>
      <td>${escHtml(p.frequence) || "-"}<br>${escHtml(p.priorite) || "-"}</td>
      <td>${escHtml(p.ressource_assignee_nom) || "-"}</td>
      <td>${escHtml(p.periode_prevue) || "-"}${
          p.duree_estimee_jours != null ? ` (${p.duree_estimee_jours} j)` : ""
        }<br>${escHtml(STATUT_COLORS[p.statut]?.label || p.statut)}</td>
    </tr>`
      )
      .join("");

    const calendrierRows = MOIS.map((mois) => {
      const lignes = planAnnuel.filter((p) => p.periode_prevue === mois);
      const contenu =
        lignes.length === 0
          ? "-"
          : lignes
              .map(
                (p) =>
                  `${escHtml(p.volet_code || `Volet #${p.volet_id}`)} (${escHtml(
                    STATUT_COLORS[p.statut]?.label || p.statut
                  )})`
              )
              .join(", ");
      return `<tr><td><strong>${mois}</strong></td><td>${contenu}</td></tr>`;
    }).join("");

    const bodyHtml = `
      <h2>Plan de contrôle ${selectedYear}</h2>
      <table>
        <thead><tr>
          <th>Volet</th><th>Périmètre</th><th>Fréquence / Priorité</th>
          <th>Ressource</th><th>Période / Statut</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <h2>Calendrier annuel</h2>
      <table>
        <thead><tr><th style="width:15%">Mois</th><th>Volets prévus</th></tr></thead>
        <tbody>${calendrierRows}</tbody>
      </table>`;

    const institutionNom = planAnnuel.find((p) => p.institution_nom)?.institution_nom;
    const kpiHtml =
      kpiTile(indicateursPlan.total, "Lignes planifiées") +
      kpiTile(indicateursPlan.realisees, "Réalisées") +
      kpiTile(indicateursPlan.enCours, "En cours") +
      kpiTile(indicateursPlan.enRetard, "En retard", indicateursPlan.enRetard > 0) +
      kpiTile(`${indicateursPlan.taux}%`, "Taux de réalisation");
    const html = buildPrintShell({
      title: `Plan de contrôle ${selectedYear}`,
      eyebrow: "ConformIMF · Contrôle & Audit",
      heading: `Plan de contrôle ${selectedYear}`,
      subtitle: `${institutionNom ? `${escHtml(institutionNom)} · ` : ""}${
        indicateursPlan.total
      } ligne(s) planifiée(s) · Taux de réalisation : ${indicateursPlan.taux}%`,
      kpiHtml,
      bodyHtml,
      landscape: true,
    });
    openPrintWindow(html, () =>
      showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning")
    );
  }

  function exporterPlanExcel() {
    if (planAnnuel.length === 0) {
      showSnack("Aucune ligne de plan à exporter.", "warning");
      return;
    }
    const rows = planAnnuel.map((p) => ({
      volet: `${p.volet_code || ""} - ${p.volet_theme || ""}`,
      perimetre: p.perimetre || "",
      frequence_priorite: [p.frequence, p.priorite].filter(Boolean).join(" / "),
      ressource_assignee_nom: p.ressource_assignee_nom || "",
      periode_statut: [
        p.periode_prevue
          ? `${p.periode_prevue}${
              p.duree_estimee_jours != null ? ` (${p.duree_estimee_jours} j)` : ""
            }`
          : "",
        STATUT_COLORS[p.statut]?.label || p.statut || "",
      ]
        .filter(Boolean)
        .join(" / "),
    }));
    const cols = [
      { key: "volet", label: "Volet" },
      { key: "perimetre", label: "Périmètre" },
      { key: "frequence_priorite", label: "Fréquence / Priorité" },
      { key: "ressource_assignee_nom", label: "Ressource" },
      { key: "periode_statut", label: "Période / Statut" },
    ];
    downloadCSV(`plan_controle_${selectedYear}`, rows, cols);
  }

  // ── Onglet C : Missions ──────────────────────────────────────────────────
  // Les missions de contrôle de cette page sont toujours internes : les
  // missions externes (CAC, CIF, Commission bancaire, BCEAO, ANSFD, DAI) ne
  // se créent et ne se suivent que depuis le Suivi des recommandations.
  const [missions, setMissions] = useState([]);
  const [loadingMissions, setLoadingMissions] = useState(false);
  const [filterAnneeMission, setFilterAnneeMission] = useState(new Date().getFullYear());

  const canCreateMission = hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE"]);

  const loadMissions = useCallback(() => {
    setLoadingMissions(true);
    const params = new URLSearchParams();
    params.append("nature", "INTERNE");
    if (filterAnneeMission) params.append("annee", filterAnneeMission);
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/missions?${params}`)
      .then((r) => setMissions(r.data))
      .catch(() => showSnack("Erreur lors du chargement des missions.", "error"))
      .finally(() => setLoadingMissions(false));
  }, [filterAnneeMission, institutionParam, showSnack]);

  useEffect(() => {
    if (activeTab === 2) loadMissions();
  }, [activeTab, loadMissions]);

  // ── Onglet D : Rapport de contrôle ──────────────────────────────────────
  const [rapportMissions, setRapportMissions] = useState([]);
  const [rapportMissionId, setRapportMissionId] = useState("");
  const [rapportData, setRapportData] = useState(null);
  const [loadingRapport, setLoadingRapport] = useState(false);
  // Contenu HTML en cours d'édition par volet (clé = mission_volet_id), initialisé
  // depuis rapport_html s'il existe déjà, sinon depuis un brouillon généré à
  // partir de l'objectif / résultat attendu / commentaire de conclusion.
  const [rapportEditState, setRapportEditState] = useState({});
  const [savingRapportVoletId, setSavingRapportVoletId] = useState(null);
  const [rapportHistorique, setRapportHistorique] = useState({});
  const [openHistoriqueVoletId, setOpenHistoriqueVoletId] = useState(null);
  // Sections de niveau mission (format du rapport d'inspection de référence,
  // outils/Rap_LBC_COTONOU_2024) : en-tête (lettre de mission, destinataires),
  // introduction, présentation de l'institution, conclusion et attestation.
  // Même principe que les chapitres par volet : brouillon généré tant que
  // rien n'a été enregistré.
  const [rapportSections, setRapportSections] = useState({});
  const [savingRapportSections, setSavingRapportSections] = useState(false);
  // RichTextEditor ne lit sa valeur qu'au montage : régénérer un brouillon
  // passe par un changement de clé (compteur par éditeur).
  const [editorVersions, setEditorVersions] = useState({});
  const [rapportLogoDataUrl, setRapportLogoDataUrl] = useState(null);
  const [rapportWorkflowBusy, setRapportWorkflowBusy] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  // Validation du rapport / envoi complémentaire au Suivi des recommandations
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    mode: "valider",
    selected: [],
  });
  // Paramètres du rapport (par institution) : direction émettrice et logo
  const [parametresDialog, setParametresDialog] = useState({
    open: false,
    direction: "",
    saving: false,
  });
  const canValiderRapport = hasAnyRole(["ADMIN_INSTITUTION", "GESTIONNAIRE"]);

  async function toggleHistorique(missionVoletId) {
    if (openHistoriqueVoletId === missionVoletId) {
      setOpenHistoriqueVoletId(null);
      return;
    }
    setOpenHistoriqueVoletId(missionVoletId);
    if (!rapportHistorique[missionVoletId]) {
      try {
        const r = await api.get(
          `/api/controle/mission-volets/${missionVoletId}/rapport/historique`
        );
        setRapportHistorique((prev) => ({ ...prev, [missionVoletId]: r.data }));
      } catch {
        showSnack("Erreur lors du chargement de l'historique.", "error");
      }
    }
  }

  useEffect(() => {
    if (activeTab !== 3) return;
    const params = new URLSearchParams();
    params.append("nature", "INTERNE");
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/missions?${params}`)
      .then((r) => setRapportMissions(r.data))
      .catch(() => {});
  }, [activeTab, institutionParam]);

  // Chapitre d'un volet : objectif, résultat attendu, constats détaillés
  // repris de la feuille de travail (points satisfaisants / insuffisances),
  // puis commentaire de conclusion — à reformuler par le contrôleur.
  function buildDefaultRapportHtml(v) {
    const nl2br = (s) => escHtml(s).replace(/\n/g, "<br>");
    const parts = [];
    if (v.objectif) parts.push(`<p><strong>Objectif :</strong> ${escHtml(v.objectif)}</p>`);
    if (v.resultat_attendu)
      parts.push(`<p><strong>Résultat attendu :</strong> ${escHtml(v.resultat_attendu)}</p>`);
    const points = v.points || [];
    const satisfaisants = points.filter((p) => p.resultat === "PAS_ANOMALIE");
    const insuffisances = points.filter((p) => p.resultat && p.resultat !== "PAS_ANOMALIE");
    if (satisfaisants.length) {
      parts.push(
        `<p><strong>Points satisfaisants :</strong></p><ul>${satisfaisants
          .map(
            (p) => `<li>${escHtml(p.exigence)}${p.constats ? ` : ${nl2br(p.constats)}` : ""} ;</li>`
          )
          .join("")}</ul>`
      );
    }
    if (insuffisances.length) {
      parts.push(
        `<p><strong>Insuffisances relevées :</strong></p><ul>${insuffisances
          .map(
            (p) =>
              `<li>${escHtml(p.exigence)} (${
                p.resultat === "ANOMALIE_MAJEURE" ? "anomalie majeure" : "anomalie mineure"
              })${p.constats ? ` : ${nl2br(p.constats)}` : ""} ;</li>`
          )
          .join("")}</ul>`
      );
    }
    if (v.commentaire_conclusion)
      parts.push(`<p><strong>Conclusion :</strong> ${nl2br(v.commentaire_conclusion)}</p>`);
    return parts.join("") || "<p></p>";
  }

  const fmtDateFr = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : null);

  function buildDefaultIntroductionHtml(r, s = {}) {
    const periode =
      r.date_debut && r.date_fin
        ? ` du ${fmtDateFr(r.date_debut)} au ${fmtDateFr(r.date_fin)}`
        : r.date_debut
        ? ` à compter du ${fmtDateFr(r.date_debut)}`
        : "";
    const lettre = s.lettre_mission_numero
      ? ` par lettre de mission n° ${escHtml(s.lettre_mission_numero)}${
          s.lettre_mission_date ? ` du ${fmtDateFr(s.lettre_mission_date)}` : ""
        }`
      : "";
    const equipe = [
      r.chef_mission_nom && `<li>${escHtml(r.chef_mission_nom)}, Chef de mission ;</li>`,
      r.controleur_affecte_nom &&
        r.controleur_affecte_nom !== r.chef_mission_nom &&
        `<li>${escHtml(r.controleur_affecte_nom)}, Contrôleur.</li>`,
    ]
      .filter(Boolean)
      .join("");
    const axes = r.volets.map((v) => `<li>${escHtml(v.volet_theme)} ;</li>`).join("");
    return (
      `<p>Dans le cadre de l'exécution du plan de contrôle annuel ${r.annee_intervention}${
        r.institution_nom ? ` de ${escHtml(r.institution_nom)}` : ""
      }, la mission ${escHtml(r.reference)} a procédé${periode} au contrôle des volets ` +
      `retenus. L'équipe commise à cet effet${lettre} est composée de :</p>` +
      (equipe ? `<ul>${equipe}</ul>` : "") +
      (axes
        ? `<p>Ainsi, les contrôles ont porté sur les axes ci-après :</p><ul>${axes}</ul>`
        : "") +
      `<p>Les résultats de cet examen, consignés au présent rapport, comprennent les ` +
      `insuffisances et lacunes identifiées accompagnées de recommandations.</p>` +
      (s.rapport_destinataires
        ? `<p>Le présent rapport est produit à l'intention de : ${escHtml(
            s.rapport_destinataires
          )}.</p>`
        : "")
    );
  }

  function buildDefaultConclusionHtml(r) {
    const nbLacunes = (r.lacunes || []).length;
    return (
      `<p>La mission s'est globalement bien déroulée et toutes les diligences prévues ont été ` +
      `mises en œuvre, ce qui a permis de :</p><ul>` +
      `<li>évaluer le fonctionnement du dispositif sur les ${r.volets.length} volet(s) contrôlé(s) ;</li>` +
      `<li>mettre en exergue ${
        nbLacunes ? `les ${nbLacunes} lacune(s)` : "les faiblesses"
      } identifiée(s) par la mission ;</li>` +
      `<li>formuler des recommandations à l'endroit des acteurs pour corriger les faiblesses ` +
      `identifiées dans le sens d'une amélioration du dispositif.</li></ul>`
    );
  }

  function initRapportSections(r) {
    const entete = {
      lettre_mission_numero: r.lettre_mission_numero || "",
      lettre_mission_date: r.lettre_mission_date || "",
      rapport_destinataires: r.rapport_destinataires || "",
    };
    setRapportSections({
      ...entete,
      rapport_introduction_html:
        r.rapport_introduction_html || buildDefaultIntroductionHtml(r, entete),
      rapport_presentation_html: r.rapport_presentation_html || "",
      rapport_conclusion_html: r.rapport_conclusion_html || buildDefaultConclusionHtml(r),
      rapport_visa_titre: r.rapport_visa_titre || "",
      rapport_visa_nom: r.rapport_visa_nom || "",
    });
  }

  const bumpEditor = (cle) =>
    setEditorVersions((prev) => ({ ...prev, [cle]: (prev[cle] || 0) + 1 }));

  // Remplace le contenu d'un éditeur par le brouillon généré (après
  // confirmation : le texte rédigé à la main serait perdu).
  function regenererSection(champ) {
    if (!rapportData) return;
    openConfirm("Remplacer le texte actuel par le brouillon généré ?", async () => {
      const html =
        champ === "rapport_introduction_html"
          ? buildDefaultIntroductionHtml(rapportData, rapportSections)
          : buildDefaultConclusionHtml(rapportData);
      setRapportSections((prev) => ({ ...prev, [champ]: html }));
      bumpEditor(champ);
    });
  }

  function regenererVolet(v) {
    openConfirm("Remplacer le texte de ce chapitre par le brouillon généré ?", async () => {
      setRapportEditState((prev) => ({
        ...prev,
        [v.mission_volet_id]: buildDefaultRapportHtml(v),
      }));
      bumpEditor(`volet-${v.mission_volet_id}`);
    });
  }

  function sectionsPayload() {
    return {
      ...rapportSections,
      lettre_mission_date: rapportSections.lettre_mission_date || null,
    };
  }

  async function saveRapportSections({ silencieux = false } = {}) {
    if (!rapportData) return false;
    setSavingRapportSections(true);
    try {
      await api.put(`/api/controle/missions/${rapportData.mission_id}/rapport`, sectionsPayload());
      if (!silencieux) showSnack("Sections du rapport enregistrées avec succès.");
      return true;
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
      return false;
    } finally {
      setSavingRapportSections(false);
    }
  }

  // Enregistre tout le rapport (sections + chapitres des volets clôturés)
  // avant de le soumettre ou de le valider : la version validée, figée en
  // base, doit être exactement celle affichée à l'écran.
  async function enregistrerToutLeRapport() {
    if (!(await saveRapportSections({ silencieux: true }))) return false;
    try {
      await Promise.all(
        rapportData.volets
          .filter((v) => v.date_cloture)
          .map((v) =>
            api.put(`/api/controle/mission-volets/${v.mission_volet_id}/rapport`, {
              rapport_html: rapportEditState[v.mission_volet_id] || buildDefaultRapportHtml(v),
            })
          )
      );
      return true;
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
      return false;
    }
  }

  async function chargerLogoRapport(data) {
    setRapportLogoDataUrl(null);
    if (!data.has_logo) return;
    try {
      const params = institutionParam ? `?institution_id=${institutionParam}` : "";
      const r = await api.get(`/api/controle/rapport-parametres/logo${params}`, {
        responseType: "blob",
      });
      const reader = new FileReader();
      reader.onload = () => setRapportLogoDataUrl(reader.result);
      reader.readAsDataURL(r.data);
    } catch {
      setRapportLogoDataUrl(null);
    }
  }

  function appliquerRapport(data, { reinitialiser = false } = {}) {
    setRapportData(data);
    if (reinitialiser) {
      const initial = {};
      data.volets.forEach((v) => {
        initial[v.mission_volet_id] = v.rapport_html || buildDefaultRapportHtml(v);
      });
      setRapportEditState(initial);
      initRapportSections(data);
      setEditorVersions({});
      chargerLogoRapport(data);
    } else {
      // Le visa peut être complété côté serveur à la validation.
      setRapportSections((prev) => ({
        ...prev,
        rapport_visa_nom: data.rapport_visa_nom || prev.rapport_visa_nom,
      }));
    }
  }

  function genererRapport() {
    if (!rapportMissionId) {
      showSnack("Sélectionnez une mission.", "warning");
      return;
    }
    setLoadingRapport(true);
    api
      .get(`/api/controle/missions/${rapportMissionId}/rapport`)
      .then((r) => appliquerRapport(r.data, { reinitialiser: true }))
      .catch((err) =>
        showSnack(err.response?.data?.detail || "Erreur lors de la génération du rapport.", "error")
      )
      .finally(() => setLoadingRapport(false));
  }

  async function actionWorkflowRapport(chemin, payload, message) {
    setRapportWorkflowBusy(true);
    try {
      const r = await api.post(
        `/api/controle/missions/${rapportData.mission_id}/rapport/${chemin}`,
        payload
      );
      appliquerRapport(r.data);
      showSnack(message);
      return true;
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'opération.", "error");
      return false;
    } finally {
      setRapportWorkflowBusy(false);
    }
  }

  async function soumettreRapport() {
    setRapportWorkflowBusy(true);
    const ok = await enregistrerToutLeRapport();
    setRapportWorkflowBusy(false);
    if (ok) await actionWorkflowRapport("soumettre", {}, "Rapport soumis à validation.");
  }

  function ouvrirValidationRapport(mode) {
    const nonEnvoyees = rapportData.lacunes.filter((l) => !l.recommandation_suivi_id);
    // Si des recommandations existent déjà dans le Suivi pour cette mission
    // (saisie manuelle), rien n'est coché d'office : risque de doublon.
    const parDefaut =
      mode === "valider" && rapportData.nb_recommandations_suivi > 0
        ? []
        : nonEnvoyees.map((l) => l.id);
    setValidationDialog({ open: true, mode, selected: parDefaut });
  }

  async function confirmerValidationRapport() {
    const { mode, selected } = validationDialog;
    if (mode === "valider") {
      setRapportWorkflowBusy(true);
      const ok = await enregistrerToutLeRapport();
      setRapportWorkflowBusy(false);
      if (!ok) return;
    }
    const reussi = await actionWorkflowRapport(
      mode === "valider" ? "valider" : "envoyer-suivi",
      { lacune_ids: selected },
      mode === "valider"
        ? `Rapport validé. ${selected.length} recommandation(s) envoyée(s) au suivi.`
        : `${selected.length} recommandation(s) envoyée(s) au suivi.`
    );
    if (reussi) setValidationDialog((prev) => ({ ...prev, open: false }));
  }

  function rouvrirRapport() {
    const valide = rapportData.rapport_statut === "VALIDE";
    openConfirm(
      valide
        ? "Annuler la validation ? Le rapport redevient modifiable (les recommandations déjà envoyées au suivi sont conservées)."
        : "Renvoyer le rapport en brouillon ?",
      () =>
        actionWorkflowRapport(
          "rouvrir",
          {},
          valide
            ? "Validation annulée : le rapport est de nouveau modifiable."
            : "Rapport renvoyé en brouillon."
        )
    );
  }

  async function exporterRapportWord() {
    if (!rapportData) return;
    setExportingDocx(true);
    try {
      const volets_html = {};
      rapportData.volets.forEach((v) => {
        volets_html[v.mission_volet_id] =
          rapportEditState[v.mission_volet_id] || buildDefaultRapportHtml(v);
      });
      const r = await api.post(
        `/api/controle/missions/${rapportData.mission_id}/rapport/docx`,
        { ...sectionsPayload(), volets_html },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rapport_controle_${rapportData.reference}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showSnack("Erreur lors de l'export Word.", "error");
    } finally {
      setExportingDocx(false);
    }
  }

  function ouvrirParametresRapport() {
    setParametresDialog({
      open: true,
      direction: rapportData?.rapport_direction || "",
      saving: false,
    });
  }

  function appliquerParametres(p) {
    setRapportData((prev) =>
      prev ? { ...prev, rapport_direction: p.rapport_direction, has_logo: p.has_logo } : prev
    );
    if (rapportData) chargerLogoRapport({ has_logo: p.has_logo });
  }

  async function enregistrerParametresRapport() {
    setParametresDialog((prev) => ({ ...prev, saving: true }));
    try {
      const r = await api.put("/api/controle/rapport-parametres", {
        rapport_direction: parametresDialog.direction,
      });
      appliquerParametres(r.data);
      showSnack("Paramètres du rapport enregistrés.");
      setParametresDialog((prev) => ({ ...prev, open: false, saving: false }));
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
      setParametresDialog((prev) => ({ ...prev, saving: false }));
    }
  }

  async function televerserLogoRapport(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/api/controle/rapport-parametres/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      appliquerParametres(r.data);
      showSnack("Logo enregistré.");
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'envoi du logo.", "error");
    }
  }

  async function supprimerLogoRapport() {
    try {
      const r = await api.delete("/api/controle/rapport-parametres/logo");
      appliquerParametres(r.data);
      showSnack("Logo supprimé.");
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de la suppression du logo.", "error");
    }
  }

  async function saveRapportVolet(missionVoletId) {
    setSavingRapportVoletId(missionVoletId);
    try {
      await api.put(`/api/controle/mission-volets/${missionVoletId}/rapport`, {
        rapport_html: rapportEditState[missionVoletId] || "",
      });
      showSnack("Chapitre du rapport enregistré avec succès.");
      setRapportData((prev) =>
        prev
          ? {
              ...prev,
              volets: prev.volets.map((v) =>
                v.mission_volet_id === missionVoletId
                  ? { ...v, rapport_html: rapportEditState[missionVoletId] }
                  : v
              ),
            }
          : prev
      );
      // Invalide l'historique déjà chargé pour ce volet (sinon la nouvelle
      // entrée qui vient d'être créée par cet enregistrement n'apparaît pas
      // tant que la page n'est pas rechargée). S'il est actuellement affiché,
      // on le recharge tout de suite plutôt que de laisser un panneau vide.
      setRapportHistorique((prev) => {
        if (!(missionVoletId in prev)) return prev;
        const next = { ...prev };
        delete next[missionVoletId];
        return next;
      });
      if (openHistoriqueVoletId === missionVoletId) {
        api
          .get(`/api/controle/mission-volets/${missionVoletId}/rapport/historique`)
          .then((r) => setRapportHistorique((prev) => ({ ...prev, [missionVoletId]: r.data })))
          .catch(() => {});
      }
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingRapportVoletId(null);
    }
  }

  function imprimerRapport() {
    if (!rapportData) return;
    // Lit le contenu courant des éditeurs (y compris les modifications non
    // encore enregistrées), comme auparavant.
    const voletsHtml = {};
    rapportData.volets.forEach((v) => {
      voletsHtml[v.mission_volet_id] =
        rapportEditState[v.mission_volet_id] || buildDefaultRapportHtml(v);
    });
    const html = buildRapportControleHtml(
      rapportData,
      rapportSections,
      voletsHtml,
      rapportLogoDataUrl
    );
    openPrintWindow(html, () =>
      showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning")
    );
  }

  function imprimerGrille() {
    if (!missionDetail || grilleData.length === 0) {
      showSnack("Aucune donnée à imprimer.", "warning");
      return;
    }
    const rows = grilleData
      .map((r) => {
        const conformite = CONFORMITE_PAR_RESULTAT[r.resultat];
        return `<tr>
      <td>${escHtml(r.volet_code)}</td>
      <td>${escHtml(r.point_code)}</td>
      <td>${escHtml(r.point_exigence)}</td>
      <td style="text-align:center">${r.point_poids}</td>
      <td style="text-align:center">${r.score != null ? escHtml(r.score) : "-"}</td>
      <td style="text-align:center">${escHtml(
        NIVEAU_RISQUE_COLORS[r.niveau_risque]?.label || r.niveau_risque || "-"
      )}</td>
      <td style="text-align:center">${escHtml(
        NIVEAU_CONFORMITE_COLORS[conformite]?.label || "-"
      )}</td>
    </tr>`;
      })
      .join("");
    const bodyHtml = `
      <h2>Grille d'évaluation</h2>
      <table>
        <thead><tr>
          <th>Volet</th><th>Code</th><th>Exigence</th>
          <th style="text-align:center">Poids</th><th style="text-align:center">Note</th>
          <th style="text-align:center">Risque</th><th style="text-align:center">Conformité</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;

    const nbConforme = grilleData.filter(
      (r) => CONFORMITE_PAR_RESULTAT[r.resultat] === "CONFORME"
    ).length;
    const nbPartiel = grilleData.filter(
      (r) => CONFORMITE_PAR_RESULTAT[r.resultat] === "PARTIELLEMENT_CONFORME"
    ).length;
    const nbNonConforme = grilleData.filter(
      (r) => CONFORMITE_PAR_RESULTAT[r.resultat] === "NON_CONFORME"
    ).length;
    const kpiHtml =
      kpiTile(grilleData.length, "Points de contrôle") +
      kpiTile(nbConforme, "Conforme") +
      kpiTile(nbPartiel, "Partiellement conforme") +
      kpiTile(nbNonConforme, "Non conforme", nbNonConforme > 0);

    const html = buildPrintShell({
      title: `Grille d'évaluation - ${missionDetail.reference}`,
      eyebrow: "ConformIMF · Contrôle & Audit",
      heading: `Grille d'évaluation - ${missionDetail.reference}`,
      subtitle: `${
        missionDetail.institution_nom ? `${escHtml(missionDetail.institution_nom)} · ` : ""
      }Chef de mission : ${escHtml(missionDetail.chef_mission_nom || "-")} · Contrôleur : ${escHtml(
        missionDetail.controleur_affecte_nom || "-"
      )}`,
      kpiHtml,
      bodyHtml,
      landscape: true,
    });
    openPrintWindow(html, () =>
      showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning")
    );
  }

  function imprimerLacunes() {
    if (!missionDetail || lacunes.length === 0) {
      showSnack("Aucune lacune à imprimer.", "warning");
      return;
    }
    const rows = lacunes
      .map(
        (l) => `<tr>
      <td>${escHtml(l.numero_ordre)}</td>
      <td><strong>${escHtml(l.volet_code)}</strong><br>${escHtml(l.critere)}</td>
      <td>${escHtml(l.constat) || "-"}</td>
      <td style="text-align:center">${escHtml(
        NIVEAU_RISQUE_COLORS[l.risque]?.label || l.risque
      )}</td>
      <td style="text-align:center">${escHtml(l.priorite)}</td>
      <td>${escHtml(l.cause) || "-"}</td>
      <td>${escHtml(l.recommandation) || "-"}</td>
      <td>${escHtml(l.delai) || "-"}</td>
      <td>${escHtml(l.responsable) || "-"}</td>
    </tr>`
      )
      .join("");
    const bodyHtml = `
      <h2>Liste des lacunes</h2>
      <table>
        <thead><tr>
          <th>N°</th><th>Volet / Critère</th><th>Constat</th>
          <th style="text-align:center">Risque</th><th style="text-align:center">Priorité</th>
          <th>Cause</th><th>Recommandation</th><th>Délai</th><th>Responsable</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;

    const nbHaute = lacunes.filter((l) => l.priorite === "Haute").length;
    const nbMoyenne = lacunes.filter((l) => l.priorite === "Moyenne").length;
    const nbFaible = lacunes.filter((l) => l.priorite === "Faible").length;
    const kpiHtml =
      kpiTile(lacunes.length, "Lacunes") +
      kpiTile(nbHaute, "Priorité haute", nbHaute > 0) +
      kpiTile(nbMoyenne, "Priorité moyenne") +
      kpiTile(nbFaible, "Priorité faible");

    const html = buildPrintShell({
      title: `Liste des lacunes - ${missionDetail.reference}`,
      eyebrow: "ConformIMF · Contrôle & Audit",
      heading: `Liste des lacunes - ${missionDetail.reference}`,
      subtitle: `${
        missionDetail.institution_nom ? `${escHtml(missionDetail.institution_nom)} · ` : ""
      }Chef de mission : ${escHtml(missionDetail.chef_mission_nom || "-")} · Contrôleur : ${escHtml(
        missionDetail.controleur_affecte_nom || "-"
      )}`,
      kpiHtml,
      bodyHtml,
      landscape: true,
    });
    openPrintWindow(html, () =>
      showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning")
    );
  }

  function exporterGrilleExcel() {
    if (!missionDetail || grilleData.length === 0) {
      showSnack("Aucune donnée à exporter.", "warning");
      return;
    }
    const rows = grilleData.map((r) => ({
      volet_code: r.volet_code,
      point_code: r.point_code,
      point_exigence: r.point_exigence,
      point_poids: r.point_poids,
      score: r.score != null ? r.score : "",
      niveau_risque: NIVEAU_RISQUE_COLORS[r.niveau_risque]?.label || r.niveau_risque || "",
      conformite: NIVEAU_CONFORMITE_COLORS[CONFORMITE_PAR_RESULTAT[r.resultat]]?.label || "",
    }));
    const cols = [
      { key: "volet_code", label: "Volet" },
      { key: "point_code", label: "Code" },
      { key: "point_exigence", label: "Exigence" },
      { key: "point_poids", label: "Poids" },
      { key: "score", label: "Note" },
      { key: "niveau_risque", label: "Risque" },
      { key: "conformite", label: "Conformité" },
    ];
    downloadCSV(`grille_evaluation_${missionDetail.reference}`, rows, cols);
  }

  function exporterLacunesExcel() {
    if (!missionDetail || lacunes.length === 0) {
      showSnack("Aucune lacune à exporter.", "warning");
      return;
    }
    const rows = lacunes.map((l) => ({
      numero_ordre: l.numero_ordre,
      volet_code: l.volet_code,
      critere: l.critere,
      constat: l.constat || "",
      niveau_risque: NIVEAU_RISQUE_COLORS[l.risque]?.label || l.risque || "",
      priorite: l.priorite,
      cause: l.cause || "",
      consequences: (l.consequences || []).join(", "),
      recommandation: l.recommandation || "",
      delai: l.delai || "",
      responsable: l.responsable || "",
    }));
    const cols = [
      { key: "numero_ordre", label: "N°" },
      { key: "volet_code", label: "Volet" },
      { key: "critere", label: "Critère" },
      { key: "constat", label: "Constat" },
      { key: "niveau_risque", label: "Risque" },
      { key: "priorite", label: "Priorité" },
      { key: "cause", label: "Cause" },
      { key: "consequences", label: "Conséquences" },
      { key: "recommandation", label: "Recommandation" },
      { key: "delai", label: "Délai" },
      { key: "responsable", label: "Responsable" },
    ];
    downloadCSV(`lacunes_${missionDetail.reference}`, rows, cols);
  }

  const [dialogMission, setDialogMission] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [formMission, setFormMission] = useState({
    annee_intervention: new Date().getFullYear(),
    plan_controle_annuel_id: "",
    chef_mission_id: "",
    controleur_affecte_id: "",
    volet_ids: [],
    date_debut: "",
    date_fin: "",
    statut: "PLANIFIEE",
  });
  const [savingMission, setSavingMission] = useState(false);
  const [lignesPlanDisponibles, setLignesPlanDisponibles] = useState([]);

  // Superviseurs : utilisateurs supplémentaires avec accès en écriture au
  // rapport de contrôle, indépendamment du chef de mission/contrôleur affecté.
  const [dialogSuperviseurs, setDialogSuperviseurs] = useState(false);
  const [formSuperviseurIds, setFormSuperviseurIds] = useState([]);
  const [savingSuperviseurs, setSavingSuperviseurs] = useState(false);

  function openEditSuperviseurs() {
    setFormSuperviseurIds((missionDetail?.superviseurs || []).map((s) => s.id));
    setDialogSuperviseurs(true);
  }

  async function saveSuperviseurs() {
    setSavingSuperviseurs(true);
    try {
      const r = await api.put(`/api/controle/missions/${missionDetail.id}/superviseurs`, {
        user_ids: formSuperviseurIds,
      });
      setMissionDetail(r.data);
      // Le rapport (autre onglet) affiche aussi la liste des superviseurs et
      // s'en sert pour le droit d'édition : le patcher évite qu'il reste
      // périmé tant que "Générer le rapport" n'est pas recliqué.
      setRapportData((prev) =>
        prev && prev.mission_id === missionDetail.id
          ? { ...prev, superviseurs: r.data.superviseurs }
          : prev
      );
      showSnack("Superviseurs mis à jour avec succès.");
      setDialogSuperviseurs(false);
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingSuperviseurs(false);
    }
  }

  useEffect(() => {
    if (!dialogMission || editingMission) return;
    const params = new URLSearchParams();
    params.append("annee", formMission.annee_intervention);
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/plan-annuel?${params}`)
      .then((r) => setLignesPlanDisponibles(r.data.filter((p) => !p.mission_liee_id)))
      .catch(() => setLignesPlanDisponibles([]));
  }, [dialogMission, editingMission, formMission.annee_intervention, institutionParam]);

  function openCreateMission() {
    setEditingMission(null);
    setFormMission({
      annee_intervention: filterAnneeMission || new Date().getFullYear(),
      plan_controle_annuel_id: "",
      chef_mission_id: "",
      controleur_affecte_id: "",
      volet_ids: [],
      date_debut: "",
      date_fin: "",
      statut: "PLANIFIEE",
    });
    setDialogMission(true);
  }

  function openEditMission(m) {
    setEditingMission(m);
    setFormMission({
      annee_intervention: m.annee_intervention,
      plan_controle_annuel_id: m.plan_controle_annuel_id || "",
      chef_mission_id: m.chef_mission_id || "",
      controleur_affecte_id: m.controleur_affecte_id || "",
      volet_ids: (m.volets || []).map((v) => v.volet_id),
      date_debut: m.date_debut || "",
      date_fin: m.date_fin || "",
      statut: m.statut,
    });
    setDialogMission(true);
  }

  function applyLignePlan(planId) {
    const ligne = lignesPlanDisponibles.find((p) => p.id === Number(planId));
    setFormMission((prev) => ({
      ...prev,
      plan_controle_annuel_id: planId,
      volet_ids: ligne ? [ligne.volet_id] : prev.volet_ids,
      controleur_affecte_id: ligne?.ressource_assignee_id || prev.controleur_affecte_id,
    }));
  }

  async function saveMission() {
    if (!editingMission && formMission.volet_ids.length === 0) {
      showSnack("Sélectionnez au moins un volet à couvrir.", "warning");
      return;
    }
    setSavingMission(true);
    try {
      if (editingMission) {
        const r = await api.put(`/api/controle/missions/${editingMission.id}`, {
          chef_mission_id: formMission.chef_mission_id || null,
          controleur_affecte_id: formMission.controleur_affecte_id || null,
          date_debut: formMission.date_debut || null,
          date_fin: formMission.date_fin || null,
          statut: formMission.statut,
        });
        if (missionDetail?.id === editingMission.id) setMissionDetail(r.data);
        showSnack("Mission mise à jour avec succès.");
      } else {
        const r = await api.post("/api/controle/missions", {
          nature: "INTERNE",
          annee_intervention: Number(formMission.annee_intervention),
          plan_controle_annuel_id: formMission.plan_controle_annuel_id || null,
          chef_mission_id: formMission.chef_mission_id || null,
          controleur_affecte_id: formMission.controleur_affecte_id || null,
          volet_ids: formMission.volet_ids,
          date_debut: formMission.date_debut || null,
          date_fin: formMission.date_fin || null,
        });
        showSnack(`Mission ${r.data.reference} créée avec succès.`);
      }
      setDialogMission(false);
      loadMissions();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingMission(false);
    }
  }

  function deleteMission(m) {
    openConfirm(`Supprimer la mission « ${m.reference} » ?`, async () => {
      try {
        await api.delete(`/api/controle/missions/${m.id}`);
        showSnack("Mission supprimée avec succès.");
        loadMissions();
      } catch (err) {
        showSnack(err.response?.data?.detail || "Erreur lors de la suppression.", "error");
      }
    });
  }

  const missionColumns = useMemo(
    () => [
      {
        Header: "Référence",
        accessor: "reference",
        width: "16%",
        Cell: ({ row }) => (
          <MDTypography variant="caption" fontWeight="bold">
            {row.original.reference}
          </MDTypography>
        ),
      },
      {
        Header: "Volets",
        accessor: "nb_volets",
        width: "9%",
        Cell: ({ row }) => row.original.nb_volets,
      },
      {
        Header: "Chef de mission",
        accessor: "chef_mission_nom",
        width: "16%",
        Cell: ({ row }) => row.original.chef_mission_nom || "-",
      },
      {
        Header: "Contrôleur",
        accessor: "controleur_affecte_nom",
        width: "16%",
        Cell: ({ row }) => row.original.controleur_affecte_nom || "-",
      },
      {
        Header: "Dates",
        accessor: "dates",
        width: "17%",
        Cell: ({ row }) => {
          const { date_debut: debut, date_fin: fin } = row.original;
          if (!debut && !fin) return "-";
          return `${debut || "?"} → ${fin || "?"}`;
        },
      },
      {
        Header: "Statut",
        accessor: "statut",
        width: "11%",
        Cell: ({ row }) => <StatutMissionChip statut={row.original.statut} />,
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "15%",
        Cell: ({ row }) => {
          const m = row.original;
          return (
            <MDBox display="flex" gap={0.5}>
              <Tooltip title="Ouvrir la feuille de travail">
                <IconButton
                  size="small"
                  onClick={() => openMissionDetail(m)}
                  sx={{ color: "#186A81" }}
                >
                  <Icon sx={{ fontSize: "1rem" }}>folder_open</Icon>
                </IconButton>
              </Tooltip>
              {canCreateMission && (
                <>
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      onClick={() => openEditMission(m)}
                      sx={{ color: "#0A3475" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                    </IconButton>
                  </Tooltip>
                  {m.statut === "PLANIFIEE" && (
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        onClick={() => deleteMission(m)}
                        sx={{ color: "#c62828" }}
                      >
                        <Icon sx={{ fontSize: "1rem" }}>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </MDBox>
          );
        },
      },
    ],
    [canCreateMission]
  );

  // ── Démarche du contrôleur : détail mission + feuille de travail ────────
  const [missionDetail, setMissionDetail] = useState(null);
  const [loadingMissionDetail, setLoadingMissionDetail] = useState(false);
  const [openVolet, setOpenVolet] = useState(null);
  const [resultatsFeuille, setResultatsFeuille] = useState([]);
  const [loadingFeuille, setLoadingFeuille] = useState(false);
  const [formResultats, setFormResultats] = useState({});
  const [savingResultatId, setSavingResultatId] = useState(null);
  const [uploadingResultatId, setUploadingResultatId] = useState(null);
  const [dialogCloture, setDialogCloture] = useState(false);
  const [commentaireCloture, setCommentaireCloture] = useState("");
  const [savingCloture, setSavingCloture] = useState(false);
  const [cloturePreview, setCloturePreview] = useState(null);

  function openDialogCloture() {
    setCloturePreview(computeVoletPreview(resultatsFeuille));
    setCommentaireCloture(genererDraftCloture(resultatsFeuille));
    setDialogCloture(true);
  }
  // Feuille de travail : un point de contrôle est ouvert à la fois dans un
  // modal ; dirtyPoints suit les points modifiés mais non encore enregistrés.
  const [openPointId, setOpenPointId] = useState(null);
  const [dirtyPoints, setDirtyPoints] = useState(() => new Set());

  // Sous-vues du détail mission : liste des volets / grille d'évaluation / lacunes
  const [missionSubView, setMissionSubView] = useState("volets");
  const [grilleData, setGrilleData] = useState([]);
  const [loadingGrille, setLoadingGrille] = useState(false);
  const [lacunes, setLacunes] = useState([]);
  const [loadingLacunes, setLoadingLacunes] = useState(false);
  const [dialogLacune, setDialogLacune] = useState(false);
  const [editingLacune, setEditingLacune] = useState(null);
  const [formLacune, setFormLacune] = useState({
    cause: "",
    consequences: [],
    recommandation: "",
    delai: "",
    responsable: "",
  });
  const [savingLacune, setSavingLacune] = useState(false);

  function openMissionDetail(m) {
    setLoadingMissionDetail(true);
    setOpenVolet(null);
    setMissionSubView("volets");
    api
      .get(`/api/controle/missions/${m.id}`)
      .then((r) => setMissionDetail(r.data))
      .catch(() => showSnack("Erreur lors du chargement de la mission.", "error"))
      .finally(() => setLoadingMissionDetail(false));
  }

  function closeMissionDetail() {
    setMissionDetail(null);
    setOpenVolet(null);
    setResultatsFeuille([]);
    setMissionSubView("volets");
  }

  function loadGrille() {
    setLoadingGrille(true);
    Promise.all(
      (missionDetail?.volets || []).map((v) =>
        api
          .get(`/api/controle/mission-volets/${v.id}/resultats`)
          .then((r) =>
            r.data.map((res) => ({ ...res, volet_code: v.volet_code, volet_theme: v.volet_theme }))
          )
      )
    )
      .then((lists) => setGrilleData(lists.flat()))
      .catch(() => showSnack("Erreur lors du chargement de la grille d'évaluation.", "error"))
      .finally(() => setLoadingGrille(false));
  }

  function loadLacunes(missionId) {
    setLoadingLacunes(true);
    api
      .get(`/api/controle/missions/${missionId}/lacunes`)
      .then((r) => setLacunes(r.data))
      .catch(() => showSnack("Erreur lors du chargement des lacunes.", "error"))
      .finally(() => setLoadingLacunes(false));
  }

  function switchMissionSubView(view) {
    setMissionSubView(view);
    if (!missionDetail) return;
    if (view === "grille") loadGrille();
    if (view === "lacunes") loadLacunes(missionDetail.id);
  }

  function openEditLacune(l) {
    setEditingLacune(l);
    setFormLacune({
      cause: l.cause || "",
      consequences: l.consequences || [],
      recommandation: l.recommandation || "",
      delai: l.delai || "",
      responsable: l.responsable || "",
    });
    setDialogLacune(true);
  }

  async function saveLacune() {
    setSavingLacune(true);
    try {
      await api.put(`/api/controle/lacunes/${editingLacune.id}`, {
        cause: formLacune.cause || null,
        consequences: formLacune.consequences,
        recommandation: formLacune.recommandation || null,
        delai: formLacune.delai || null,
        responsable: formLacune.responsable || null,
      });
      showSnack("Lacune mise à jour avec succès.");
      setDialogLacune(false);
      if (missionDetail) loadLacunes(missionDetail.id);
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingLacune(false);
    }
  }

  function fillFormResultats(list) {
    const next = {};
    list.forEach((r) => {
      next[r.id] = {
        echantillon_population: r.echantillon_population || "",
        echantillon_nature: r.echantillon_nature || "",
        observations: r.observations || "",
        resultat: r.resultat || "",
        constats: r.constats || "",
        recommandation: r.recommandation || "",
      };
    });
    setFormResultats(next);
  }

  function openVoletFeuille(volet) {
    setOpenVolet(volet);
    setOpenPointId(null);
    setDirtyPoints(new Set());
    setLoadingFeuille(true);
    api
      .get(`/api/controle/mission-volets/${volet.id}/resultats`)
      .then((r) => {
        setResultatsFeuille(r.data);
        fillFormResultats(r.data);
      })
      .catch(() => showSnack("Erreur lors du chargement de la feuille de travail.", "error"))
      .finally(() => setLoadingFeuille(false));
  }

  function backToMissionDetail() {
    setOpenVolet(null);
    setOpenPointId(null);
    setDirtyPoints(new Set());
    setResultatsFeuille([]);
    if (missionDetail) openMissionDetail(missionDetail);
  }

  function updateFormResultat(resultatId, field, value) {
    setFormResultats((prev) => ({
      ...prev,
      [resultatId]: { ...prev[resultatId], [field]: value },
    }));
    setDirtyPoints((prev) => {
      if (prev.has(resultatId)) return prev;
      const next = new Set(prev);
      next.add(resultatId);
      return next;
    });
  }

  function clearDirtyPoint(resultatId) {
    setDirtyPoints((prev) => {
      if (!prev.has(resultatId)) return prev;
      const next = new Set(prev);
      next.delete(resultatId);
      return next;
    });
  }

  async function saveResultat(resultatId) {
    const form = formResultats[resultatId] || {};
    setSavingResultatId(resultatId);
    try {
      const r = await api.put(`/api/controle/resultats-controle/${resultatId}`, {
        echantillon_population: form.echantillon_population || null,
        echantillon_nature: form.echantillon_nature || null,
        observations: form.observations || null,
        resultat: form.resultat || null,
        constats: form.constats || null,
        recommandation: form.recommandation || null,
      });
      setResultatsFeuille((prev) => prev.map((x) => (x.id === resultatId ? r.data : x)));
      clearDirtyPoint(resultatId);
      showSnack("Résultat enregistré avec succès.");
      return true;
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
      return false;
    } finally {
      setSavingResultatId(null);
    }
  }

  // ── Navigation entre points de contrôle dans le modal ─────────────────────
  function navigateToPointIndex(idx) {
    if (idx >= 0 && idx < resultatsFeuille.length) setOpenPointId(resultatsFeuille[idx].id);
  }

  function stepPoint(delta) {
    const idx = resultatsFeuille.findIndex((r) => r.id === openPointId);
    if (idx < 0) return;
    const target = idx + delta;
    if (dirtyPoints.has(openPointId)) {
      openConfirm(
        "Des modifications ne sont pas enregistrées sur ce point. Continuer sans enregistrer ?",
        () => navigateToPointIndex(target)
      );
      return;
    }
    navigateToPointIndex(target);
  }

  function requestClosePoint() {
    if (openPointId != null && dirtyPoints.has(openPointId)) {
      openConfirm(
        "Des modifications ne sont pas enregistrées sur ce point. Fermer sans enregistrer ?",
        () => setOpenPointId(null)
      );
      return;
    }
    setOpenPointId(null);
  }

  async function saveAndNextPoint() {
    const idx = resultatsFeuille.findIndex((r) => r.id === openPointId);
    const ok = await saveResultat(openPointId);
    if (ok) navigateToPointIndex(idx + 1);
  }

  async function uploadPreuve(resultatId, file) {
    const fd = new FormData();
    fd.append("file", file);
    setUploadingResultatId(resultatId);
    try {
      await api.post(`/api/controle/resultats-controle/${resultatId}/preuves`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showSnack("Preuve téléversée avec succès.");
      const r = await api.get(`/api/controle/mission-volets/${openVolet.id}/resultats`);
      setResultatsFeuille(r.data);
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors du téléversement.", "error");
    } finally {
      setUploadingResultatId(null);
    }
  }

  function deletePreuve(preuveId, resultatId) {
    openConfirm("Supprimer cette preuve ?", async () => {
      try {
        await api.delete(`/api/controle/preuves-controle/${preuveId}`);
        setResultatsFeuille((prev) =>
          prev.map((r) =>
            r.id === resultatId ? { ...r, preuves: r.preuves.filter((p) => p.id !== preuveId) } : r
          )
        );
        showSnack("Preuve supprimée avec succès.");
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  async function saveCloture() {
    if (!commentaireCloture.trim()) {
      showSnack("Le commentaire de conclusion est obligatoire.", "warning");
      return;
    }
    setSavingCloture(true);
    try {
      const r = await api.post(`/api/controle/mission-volets/${openVolet.id}/cloturer`, {
        commentaire_conclusion: commentaireCloture.trim(),
      });
      setOpenVolet(r.data);
      setOpenPointId(null);
      setDirtyPoints(new Set());
      setDialogCloture(false);
      showSnack("Volet clôturé avec succès.");
      if (missionDetail) {
        api
          .get(`/api/controle/missions/${missionDetail.id}`)
          .then((res) => setMissionDetail(res.data))
          .catch(() => {});
      }
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de la clôture.", "error");
    } finally {
      setSavingCloture(false);
    }
  }

  function reouvrirVolet() {
    openConfirm(
      `Rouvrir le volet « ${openVolet.volet_code} » ? Le taux de conformité, le niveau de conformité et le niveau de risque seront effacés jusqu'à la prochaine clôture.`,
      async () => {
        try {
          const r = await api.post(`/api/controle/mission-volets/${openVolet.id}/rouvrir`);
          setOpenVolet(r.data);
          showSnack("Volet rouvert avec succès.");
          if (missionDetail) {
            api
              .get(`/api/controle/missions/${missionDetail.id}`)
              .then((res) => setMissionDetail(res.data))
              .catch(() => {});
          }
        } catch (err) {
          showSnack(err.response?.data?.detail || "Erreur lors de la réouverture.", "error");
        }
      }
    );
  }

  // Corps du formulaire d'un point de contrôle, affiché dans le modal
  // (le titre code + exigence et les boutons de navigation sont gérés par le Dialog).
  function renderCarteResultat(resultat, isClosed) {
    const form = formResultats[resultat.id] || {};
    const isUploading = uploadingResultatId === resultat.id;
    const liveRisque = form.resultat ? RISQUE_PAR_RESULTAT[form.resultat] : null;
    const canEditCard = canEditResultats && !isClosed;

    return (
      <MDBox key={resultat.id}>
        <MDBox>
          <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
            <Chip
              label={resultat.point_code}
              size="small"
              sx={{ fontWeight: 700, bgcolor: "#0A3475", color: "#fff" }}
            />
            <MDTypography variant="button" fontWeight="medium" sx={{ flexGrow: 1 }}>
              {resultat.point_exigence}
            </MDTypography>
            <Chip
              label={resultat.point_poids}
              size="small"
              sx={{
                bgcolor: `${POIDS_COLOR[resultat.point_poids]}22`,
                color: POIDS_COLOR[resultat.point_poids],
                fontWeight: 700,
              }}
            />
            {liveRisque && (
              <Chip
                label={NIVEAU_RISQUE_COLORS[liveRisque].label}
                size="small"
                sx={{
                  bgcolor: NIVEAU_RISQUE_COLORS[liveRisque].bg,
                  color: NIVEAU_RISQUE_COLORS[liveRisque].color,
                  fontWeight: 700,
                }}
              />
            )}
          </MDBox>

          {(resultat.point_test_audit || resultat.point_preuve_attendue) && (
            <MDBox sx={{ background: "#fafbfc", borderRadius: "8px", p: 1.5, mb: 2 }}>
              {resultat.point_test_audit && (
                <MDTypography variant="caption" sx={{ display: "block", color: "#5c6b7f" }}>
                  <strong>Test d&apos;audit :</strong> {resultat.point_test_audit}
                </MDTypography>
              )}
              {resultat.point_preuve_attendue && (
                <MDTypography
                  variant="caption"
                  sx={{ display: "block", color: "#5c6b7f", mt: 0.5 }}
                >
                  <strong>Preuve attendue :</strong> {resultat.point_preuve_attendue}
                </MDTypography>
              )}
            </MDBox>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Échantillon - population"
                disabled={!canEditCard}
                value={form.echantillon_population || ""}
                onChange={(e) =>
                  updateFormResultat(resultat.id, "echantillon_population", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!canEditCard}>
                <InputLabel shrink>Nature de l&apos;échantillon</InputLabel>
                <Select
                  label="Nature de l'échantillon"
                  displayEmpty
                  notched
                  value={form.echantillon_nature || ""}
                  onChange={(e) =>
                    updateFormResultat(resultat.id, "echantillon_nature", e.target.value)
                  }
                >
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value="ALEATOIRE">Aléatoire</MenuItem>
                  <MenuItem value="CIBLE">Ciblé</MenuItem>
                  <MenuItem value="BASE_RISQUE">Basé sur les risques</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            size="small"
            label="Observations"
            multiline
            rows={2}
            sx={{ mt: 2 }}
            disabled={!canEditCard}
            value={form.observations || ""}
            onChange={(e) => updateFormResultat(resultat.id, "observations", e.target.value)}
          />

          <MDBox mt={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#0A3475" }}>
              Résultat
            </MDTypography>
            <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
              {RESULTAT_OPTIONS.map((opt) => (
                <MDButton
                  key={opt.value}
                  size="small"
                  variant={form.resultat === opt.value ? "gradient" : "outlined"}
                  color={opt.color}
                  disabled={!canEditCard}
                  onClick={() => updateFormResultat(resultat.id, "resultat", opt.value)}
                >
                  {opt.label}
                </MDButton>
              ))}
            </MDBox>
          </MDBox>

          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Constats"
                multiline
                rows={2}
                sx={{ mt: 2 }}
                disabled={!canEditCard}
                value={form.constats || ""}
                onChange={(e) => updateFormResultat(resultat.id, "constats", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Recommandation"
                multiline
                rows={2}
                sx={{ mt: 2 }}
                disabled={!canEditCard}
                value={form.recommandation || ""}
                onChange={(e) => updateFormResultat(resultat.id, "recommandation", e.target.value)}
              />
            </Grid>
          </Grid>

          <MDBox mt={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#0A3475" }}>
              Preuves
            </MDTypography>
            <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap" alignItems="center">
              {resultat.preuves.map((p) => (
                <Chip
                  key={p.id}
                  icon={
                    <Icon sx={{ fontSize: "1rem !important" }}>
                      {PREUVE_ICON[p.type_fichier] || "insert_drive_file"}
                    </Icon>
                  }
                  label={p.nom_fichier}
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    window.open(
                      `${api.defaults.baseURL}/api/controle/preuves-controle/${p.id}/fichier`,
                      "_blank"
                    )
                  }
                  onDelete={canEditCard ? () => deletePreuve(p.id, resultat.id) : undefined}
                />
              ))}
              {canEditCard && (
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  component="label"
                  disabled={isUploading}
                  startIcon={
                    isUploading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Icon>upload_file</Icon>
                    )
                  }
                >
                  Ajouter une preuve
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.csv"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) uploadPreuve(resultat.id, file);
                      e.target.value = "";
                    }}
                  />
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  }

  // Modal d'un point de contrôle : formulaire complet + navigation Précédent / Suivant.
  function renderPointDialog(isClosed) {
    const idx = resultatsFeuille.findIndex((r) => r.id === openPointId);
    const current = idx >= 0 ? resultatsFeuille[idx] : null;
    const isSaving = current ? savingResultatId === current.id : false;
    const canEditCard = canEditResultats && !isClosed;
    const isLast = idx === resultatsFeuille.length - 1;

    return (
      <Dialog open={!!current} onClose={requestClosePoint} maxWidth="md" fullWidth scroll="paper">
        {current && (
          <>
            <DialogTitle
              sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", pr: 6 }}
            >
              <Chip
                label={current.point_code}
                size="small"
                sx={{ fontWeight: 700, bgcolor: "#0A3475", color: "#fff" }}
              />
              <MDTypography variant="h6" sx={{ flexGrow: 1, color: "#0A3475" }}>
                {current.point_exigence}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#5c6b7f" }}>
                {idx + 1} / {resultatsFeuille.length}
              </MDTypography>
              <IconButton
                size="small"
                onClick={requestClosePoint}
                sx={{ position: "absolute", right: 8, top: 8, color: "#5c6b7f" }}
              >
                <Icon>close</Icon>
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>{renderCarteResultat(current, isClosed)}</DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                disabled={idx <= 0}
                onClick={() => stepPoint(-1)}
              >
                <Icon>chevron_left</Icon>&nbsp;Précédent
              </MDButton>
              <MDBox display="flex" gap={1}>
                {canEditCard && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    disabled={isSaving}
                    onClick={() => saveResultat(current.id)}
                  >
                    {isSaving ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
                  </MDButton>
                )}
                {isLast ? (
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="small"
                    disabled={isSaving}
                    onClick={async () => {
                      if (!canEditCard) {
                        requestClosePoint();
                        return;
                      }
                      const ok = await saveResultat(current.id);
                      if (ok) setOpenPointId(null);
                    }}
                  >
                    {canEditCard ? "Enregistrer et terminer" : "Fermer"}
                  </MDButton>
                ) : (
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    disabled={isSaving}
                    onClick={() => (canEditCard ? saveAndNextPoint() : stepPoint(1))}
                  >
                    {canEditCard ? "Enregistrer et suivant" : "Suivant"}
                    &nbsp;<Icon>chevron_right</Icon>
                  </MDButton>
                )}
              </MDBox>
            </DialogActions>
          </>
        )}
      </Dialog>
    );
  }

  function renderFeuilleTravail() {
    const allExecuted = resultatsFeuille.length > 0 && resultatsFeuille.every((r) => r.resultat);
    const nbExecutes = resultatsFeuille.filter((r) => r.resultat).length;
    const isClosed = !!openVolet.date_cloture;

    return (
      <MDBox>
        <MDBox display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <IconButton size="small" onClick={backToMissionDetail} sx={{ color: "#0A3475" }}>
            <Icon>arrow_back</Icon>
          </IconButton>
          <MDTypography variant="h6" sx={{ color: "#0A3475" }}>
            {openVolet.volet_code} - {openVolet.volet_theme}
          </MDTypography>
          {isClosed && (
            <Chip
              label="Volet clôturé"
              size="small"
              sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 700 }}
            />
          )}
        </MDBox>

        {isClosed && (
          <MDBox
            sx={{
              background: "#f8f9fa",
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              p: 2,
              mb: 3,
            }}
          >
            <MDBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
              <Chip label={`Taux de conformité : ${openVolet.taux_conformite}%`} size="small" />
              <Chip
                label={NIVEAU_CONFORMITE_COLORS[openVolet.niveau_conformite]?.label}
                size="small"
                sx={{
                  bgcolor: NIVEAU_CONFORMITE_COLORS[openVolet.niveau_conformite]?.bg,
                  color: NIVEAU_CONFORMITE_COLORS[openVolet.niveau_conformite]?.color,
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`Risque : ${NIVEAU_RISQUE_COLORS[openVolet.niveau_risque]?.label}`}
                size="small"
                sx={{
                  bgcolor: NIVEAU_RISQUE_COLORS[openVolet.niveau_risque]?.bg,
                  color: NIVEAU_RISQUE_COLORS[openVolet.niveau_risque]?.color,
                  fontWeight: 700,
                }}
              />
            </MDBox>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#0A3475" }}>
              Commentaire de conclusion
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#5c6b7f", mb: canCreateMission ? 1.5 : 0 }}>
              {openVolet.commentaire_conclusion}
            </MDTypography>
            {canCreateMission && (
              <MDButton size="small" variant="outlined" color="warning" onClick={reouvrirVolet}>
                <Icon sx={{ mr: 0.5 }}>lock_open</Icon> Rouvrir le volet
              </MDButton>
            )}
          </MDBox>
        )}

        {loadingFeuille ? (
          <MDBox display="flex" justifyContent="center" p={5}>
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              mb={1.5}
            >
              <MDTypography variant="button" fontWeight="medium" sx={{ color: "#5c6b7f" }}>
                {nbExecutes} / {resultatsFeuille.length} point
                {resultatsFeuille.length > 1 ? "s" : ""} exécuté
                {nbExecutes > 1 ? "s" : ""}
              </MDTypography>
              {canEditResultats && !isClosed && (
                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  disabled={!allExecuted}
                  onClick={openDialogCloture}
                >
                  <Icon sx={{ mr: 0.5 }}>task_alt</Icon> Clôturer le volet
                </MDButton>
              )}
            </MDBox>
            {canEditResultats && !isClosed && !allExecuted && (
              <MDTypography variant="caption" sx={{ color: "#c62828", display: "block", mb: 1 }}>
                Tous les points de contrôle doivent être exécutés avant la clôture.
              </MDTypography>
            )}

            {resultatsFeuille.length === 0 && (
              <MDTypography variant="body2" sx={{ color: "#5c6b7f", p: 2 }}>
                Aucun point de contrôle rattaché à ce volet.
              </MDTypography>
            )}

            {resultatsFeuille.map((r) => {
              const done = !!r.resultat;
              const risque = done ? RISQUE_PAR_RESULTAT[r.resultat] : null;
              return (
                <Card
                  key={r.id}
                  onClick={() => setOpenPointId(r.id)}
                  sx={{
                    mb: 1,
                    border: "1px solid #e0e0e0",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#0A3475",
                      boxShadow: "0 2px 8px rgba(10,52,117,0.12)",
                    },
                  }}
                >
                  <MDBox p={1.5} display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Chip
                      label={r.point_code}
                      size="small"
                      sx={{ fontWeight: 700, bgcolor: "#0A3475", color: "#fff" }}
                    />
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{ flexGrow: 1, minWidth: 160 }}
                    >
                      {r.point_exigence}
                    </MDTypography>
                    <Chip
                      label={r.point_poids}
                      size="small"
                      sx={{
                        bgcolor: `${POIDS_COLOR[r.point_poids]}22`,
                        color: POIDS_COLOR[r.point_poids],
                        fontWeight: 700,
                      }}
                    />
                    {risque && (
                      <Chip
                        label={NIVEAU_RISQUE_COLORS[risque].label}
                        size="small"
                        sx={{
                          bgcolor: NIVEAU_RISQUE_COLORS[risque].bg,
                          color: NIVEAU_RISQUE_COLORS[risque].color,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    <Chip
                      icon={
                        <Icon sx={{ fontSize: "1rem !important" }}>
                          {done ? "check_circle" : "radio_button_unchecked"}
                        </Icon>
                      }
                      label={done ? "Exécuté" : "À faire"}
                      size="small"
                      sx={{
                        bgcolor: done ? "#e8f5e9" : "#f5f5f5",
                        color: done ? "#2e7d32" : "#757575",
                        fontWeight: 700,
                      }}
                    />
                    {dirtyPoints.has(r.id) && (
                      <Chip
                        label="Non enregistré"
                        size="small"
                        sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700 }}
                      />
                    )}
                    <IconButton size="small" sx={{ color: "#0A3475" }}>
                      <Icon>{isClosed ? "visibility" : "edit"}</Icon>
                    </IconButton>
                  </MDBox>
                </Card>
              );
            })}

            {renderPointDialog(isClosed)}
          </>
        )}
      </MDBox>
    );
  }

  function renderGrilleEvaluation() {
    if (loadingGrille) {
      return (
        <MDBox display="flex" justifyContent="center" p={5}>
          <CircularProgress color="info" />
        </MDBox>
      );
    }
    if (grilleData.length === 0) {
      return (
        <MDTypography variant="body2" color="text">
          Aucun résultat saisi pour le moment.
        </MDTypography>
      );
    }
    const columns = [
      { Header: "Volet", accessor: "volet_code", width: "10%" },
      { Header: "Code", accessor: "point_code", width: "8%" },
      { Header: "Exigence", accessor: "point_exigence", width: "32%", wrap: true },
      {
        Header: "Poids",
        accessor: "point_poids",
        width: "8%",
        Cell: ({ row }) => (
          <Chip
            label={row.original.point_poids}
            size="small"
            sx={{
              bgcolor: `${POIDS_COLOR[row.original.point_poids]}22`,
              color: POIDS_COLOR[row.original.point_poids],
              fontWeight: 700,
            }}
          />
        ),
      },
      {
        Header: "Note",
        accessor: "score",
        width: "10%",
        Cell: ({ row }) => (row.original.score != null ? row.original.score : "-"),
      },
      {
        Header: "Risque",
        accessor: "niveau_risque",
        width: "12%",
        Cell: ({ row }) => {
          const r = row.original.niveau_risque;
          if (!r) return "-";
          return (
            <Chip
              label={NIVEAU_RISQUE_COLORS[r]?.label}
              size="small"
              sx={{
                bgcolor: NIVEAU_RISQUE_COLORS[r]?.bg,
                color: NIVEAU_RISQUE_COLORS[r]?.color,
                fontWeight: 700,
              }}
            />
          );
        },
      },
      {
        Header: "Conformité",
        accessor: "resultat",
        width: "12%",
        Cell: ({ row }) => {
          const c = CONFORMITE_PAR_RESULTAT[row.original.resultat];
          if (!c) return "-";
          return (
            <Chip
              label={NIVEAU_CONFORMITE_COLORS[c]?.label}
              size="small"
              sx={{
                bgcolor: NIVEAU_CONFORMITE_COLORS[c]?.bg,
                color: NIVEAU_CONFORMITE_COLORS[c]?.color,
                fontWeight: 700,
              }}
            />
          );
        },
      },
    ];
    return (
      <MDBox>
        <MDBox display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <MDButton size="small" variant="outlined" color="success" onClick={exporterGrilleExcel}>
            <Icon sx={{ mr: 0.5 }}>download</Icon> Excel
          </MDButton>
          <MDButton size="small" variant="outlined" color="info" onClick={imprimerGrille}>
            <Icon sx={{ mr: 0.5 }}>print</Icon> Imprimer
          </MDButton>
        </MDBox>
        <DataTable table={{ columns, rows: grilleData }} isSorted entriesPerPage showTotalEntries />
      </MDBox>
    );
  }

  function renderLacunesTab() {
    if (loadingLacunes) {
      return (
        <MDBox display="flex" justifyContent="center" p={5}>
          <CircularProgress color="info" />
        </MDBox>
      );
    }
    if (lacunes.length === 0) {
      return (
        <MDTypography variant="body2" color="text">
          Aucune lacune : aucune exigence en anomalie n&apos;a été relevée pour le moment.
        </MDTypography>
      );
    }
    const columns = [
      { Header: "N°", accessor: "numero_ordre", width: "6%" },
      {
        Header: "Volet / Critère",
        accessor: "critere",
        width: "26%",
        wrap: true,
        Cell: ({ row }) => (
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" sx={{ display: "block" }}>
              {row.original.volet_code}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.original.critere}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Constat",
        accessor: "constat",
        width: "20%",
        wrap: true,
        Cell: ({ row }) => row.original.constat || "-",
      },
      {
        Header: "Risque",
        accessor: "risque",
        width: "9%",
        Cell: ({ row }) => (
          <Chip
            label={NIVEAU_RISQUE_COLORS[row.original.risque]?.label || row.original.risque}
            size="small"
            sx={{
              bgcolor: NIVEAU_RISQUE_COLORS[row.original.risque]?.bg,
              color: NIVEAU_RISQUE_COLORS[row.original.risque]?.color,
              fontWeight: 700,
            }}
          />
        ),
      },
      {
        Header: "Priorité",
        accessor: "priorite",
        width: "9%",
        Cell: ({ row }) => (
          <Chip
            label={row.original.priorite}
            size="small"
            sx={{
              bgcolor: PRIORITE_LACUNE_COLORS[row.original.priorite]?.bg,
              color: PRIORITE_LACUNE_COLORS[row.original.priorite]?.color,
              fontWeight: 700,
            }}
          />
        ),
      },
      {
        Header: "Cause / Responsable",
        accessor: "cause",
        width: "18%",
        wrap: true,
        Cell: ({ row }) => (
          <MDBox>
            <MDTypography variant="caption" sx={{ display: "block" }}>
              {row.original.cause || "-"}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.original.responsable || "-"}
              {row.original.delai ? ` · ${row.original.delai}` : ""}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "8%",
        Cell: ({ row }) => {
          if (!canEditResultats) return null;
          return (
            <Tooltip title="Compléter la lacune">
              <IconButton
                size="small"
                onClick={() => openEditLacune(row.original)}
                sx={{ color: "#0A3475" }}
              >
                <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
              </IconButton>
            </Tooltip>
          );
        },
      },
    ];
    return (
      <MDBox>
        <MDBox display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <MDButton size="small" variant="outlined" color="success" onClick={exporterLacunesExcel}>
            <Icon sx={{ mr: 0.5 }}>download</Icon> Excel
          </MDButton>
          <MDButton size="small" variant="outlined" color="info" onClick={imprimerLacunes}>
            <Icon sx={{ mr: 0.5 }}>print</Icon> Imprimer
          </MDButton>
        </MDBox>
        <DataTable table={{ columns, rows: lacunes }} isSorted entriesPerPage showTotalEntries />
      </MDBox>
    );
  }

  function renderMissionDetail() {
    const m = missionDetail;
    return (
      <MDBox>
        <MDBox display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
          <IconButton size="small" onClick={closeMissionDetail} sx={{ color: "#0A3475" }}>
            <Icon>arrow_back</Icon>
          </IconButton>
          <MDTypography variant="h6" sx={{ color: "#0A3475" }}>
            {m.reference}
          </MDTypography>
          <StatutMissionChip statut={m.statut} />
          {canCreateMission && (
            <Tooltip title="Modifier la mission (statut, dates, chef, contrôleur)">
              <IconButton size="small" onClick={() => openEditMission(m)} sx={{ color: "#0A3475" }}>
                <Icon sx={{ fontSize: "1.1rem" }}>edit</Icon>
              </IconButton>
            </Tooltip>
          )}
        </MDBox>

        <MDBox display="flex" gap={4} flexWrap="wrap" mb={3}>
          <MDBox>
            <MDTypography variant="caption" color="text">
              Chef de mission
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {m.chef_mission_nom || "-"}
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" color="text">
              Contrôleur affecté
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {m.controleur_affecte_nom || "-"}
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" color="text">
              Période
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {m.date_debut || "?"} → {m.date_fin || "?"}
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <MDTypography variant="caption" color="text">
                Superviseurs
              </MDTypography>
              {canCreateMission && (
                <IconButton
                  size="small"
                  onClick={openEditSuperviseurs}
                  sx={{ color: "#0A3475", p: 0.2 }}
                >
                  <Icon sx={{ fontSize: "0.95rem" }}>edit</Icon>
                </IconButton>
              )}
            </MDBox>
            <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={0.3}>
              {(m.superviseurs || []).length === 0 ? (
                <MDTypography variant="body2" fontWeight="medium" color="text">
                  -
                </MDTypography>
              ) : (
                m.superviseurs.map((s) => (
                  <Chip key={s.id} label={s.nom} size="small" variant="outlined" />
                ))
              )}
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox display="flex" gap={1} mb={2.5} flexWrap="wrap">
          {[
            { key: "volets", label: "Volets", icon: "view_list" },
            { key: "grille", label: "Grille d'évaluation", icon: "grid_view" },
            { key: "lacunes", label: "Lacunes", icon: "report_problem" },
          ].map((tab) => (
            <MDButton
              key={tab.key}
              size="small"
              variant={missionSubView === tab.key ? "gradient" : "outlined"}
              color="info"
              onClick={() => switchMissionSubView(tab.key)}
            >
              <Icon sx={{ mr: 0.5, fontSize: "1.1rem !important" }}>{tab.icon}</Icon>
              {tab.label}
            </MDButton>
          ))}
        </MDBox>

        {missionSubView === "volets" && (
          <>
            <MDTypography
              variant="button"
              fontWeight="bold"
              sx={{ color: "#0A3475", display: "block", mb: 1.5 }}
            >
              Volets de la mission
            </MDTypography>
            {(m.volets || []).length === 0 ? (
              <MDTypography variant="body2" color="text">
                Aucun volet sur cette mission.
              </MDTypography>
            ) : (
              <Grid container spacing={2}>
                {m.volets.map((v) => {
                  const closed = !!v.date_cloture;
                  const progress =
                    v.nb_points > 0 ? Math.round((v.nb_executes / v.nb_points) * 100) : 0;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={v.id}>
                      <Card
                        onClick={() => openVoletFeuille(v)}
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          height: "100%",
                          border: "1px solid #e0e0e0",
                          transition: "box-shadow 0.2s",
                          "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.1)" },
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            label={v.volet_code}
                            size="small"
                            sx={{ fontWeight: 700, bgcolor: "#0A3475", color: "#fff" }}
                          />
                          <Chip
                            label={closed ? "Clôturé" : "En cours"}
                            size="small"
                            sx={
                              closed
                                ? { bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 700 }
                                : { bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700 }
                            }
                          />
                        </MDBox>
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          sx={{ display: "block", mb: 1 }}
                        >
                          {v.volet_theme}
                        </MDTypography>
                        {closed ? (
                          <MDBox display="flex" gap={0.5} flexWrap="wrap">
                            <Chip
                              label={NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.label}
                              size="small"
                              sx={{
                                bgcolor: NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.bg,
                                color: NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.color,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                              }}
                            />
                            <Chip
                              label={NIVEAU_RISQUE_COLORS[v.niveau_risque]?.label}
                              size="small"
                              sx={{
                                bgcolor: NIVEAU_RISQUE_COLORS[v.niveau_risque]?.bg,
                                color: NIVEAU_RISQUE_COLORS[v.niveau_risque]?.color,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                              }}
                            />
                          </MDBox>
                        ) : (
                          <MDTypography variant="caption" color="text">
                            {v.nb_executes}/{v.nb_points} exigences exécutées ({progress}%)
                          </MDTypography>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        {missionSubView === "grille" && renderGrilleEvaluation()}
        {missionSubView === "lacunes" && renderLacunesTab()}
      </MDBox>
    );
  }

  // Section de niveau mission du rapport (introduction, présentation,
  // conclusion) : enregistrée avec les autres via saveRapportSections.
  // onRegenerer : bouton « Régénérer » (brouillon calculé depuis la mission).
  function renderSectionRapport(titre, champ, canEdit, aide, onRegenerer) {
    return (
      <MDBox mb={3}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
            {titre}
          </MDTypography>
          {canEdit && onRegenerer && (
            <MDButton
              size="small"
              variant="text"
              color="secondary"
              onClick={onRegenerer}
              sx={{ fontSize: "0.7rem", py: 0 }}
            >
              <Icon sx={{ fontSize: "1rem", mr: 0.3 }}>autorenew</Icon>
              Régénérer
            </MDButton>
          )}
        </MDBox>
        {aide && (
          <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
            {aide}
          </MDTypography>
        )}
        <MDBox mt={1}>
          <RichTextEditor
            key={`${rapportData.mission_id}-${champ}-${editorVersions[champ] || 0}`}
            value={rapportSections[champ]}
            onChange={(html) => setRapportSections((prev) => ({ ...prev, [champ]: html }))}
            disabled={!canEdit}
          />
        </MDBox>
      </MDBox>
    );
  }

  const STATUT_RAPPORT = {
    BROUILLON: { label: "Brouillon", bg: "#eceff1", color: "#455a64" },
    SOUMIS: { label: "Soumis à validation", bg: "#fff3e0", color: "#e65100" },
    VALIDE: { label: "Validé", bg: "#e8f5e9", color: "#2e7d32" },
  };

  function renderRapportWorkflow(canEditRapport) {
    const statut = rapportData.rapport_statut || "BROUILLON";
    const st = STATUT_RAPPORT[statut];
    const voletsOuverts = rapportData.volets.filter((v) => !v.date_cloture).length;
    const nonEnvoyees = rapportData.lacunes.filter((l) => !l.recommandation_suivi_id).length;
    return (
      <MDBox
        mb={3}
        p={1.5}
        display="flex"
        alignItems="center"
        gap={1.5}
        flexWrap="wrap"
        sx={{ bgcolor: "#f8f9fa", borderRadius: "8px" }}
      >
        <Chip
          label={st.label}
          size="small"
          sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700 }}
        />
        <MDTypography variant="caption" color="text" sx={{ flex: 1, minWidth: 220 }}>
          {statut === "VALIDE" &&
            `Validé le ${fmtDateFr(rapportData.rapport_valide_le)}${
              rapportData.rapport_valide_par_nom ? ` par ${rapportData.rapport_valide_par_nom}` : ""
            } : le rapport, ses chapitres et ses lacunes sont figés.`}
          {statut === "SOUMIS" &&
            `Soumis le ${fmtDateFr(rapportData.rapport_soumis_le)}${
              rapportData.rapport_soumis_par_nom ? ` par ${rapportData.rapport_soumis_par_nom}` : ""
            }, en attente de validation.`}
          {statut === "BROUILLON" &&
            "En cours de rédaction. Une fois prêt, soumettez-le à validation."}
          {statut !== "VALIDE" &&
            voletsOuverts > 0 &&
            ` ${voletsOuverts} volet(s) non clôturé(s) : la validation n'est possible qu'une fois tous les volets clôturés.`}
        </MDTypography>
        {statut === "BROUILLON" && canEditRapport && (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            disabled={rapportWorkflowBusy}
            onClick={soumettreRapport}
          >
            <Icon sx={{ mr: 0.5 }}>send</Icon> Soumettre à validation
          </MDButton>
        )}
        {statut !== "VALIDE" && canValiderRapport && (
          <MDButton
            size="small"
            variant="gradient"
            color="success"
            disabled={rapportWorkflowBusy || voletsOuverts > 0}
            onClick={() => ouvrirValidationRapport("valider")}
          >
            <Icon sx={{ mr: 0.5 }}>verified</Icon> Valider le rapport
          </MDButton>
        )}
        {statut === "VALIDE" && canValiderRapport && nonEnvoyees > 0 && (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            disabled={rapportWorkflowBusy}
            onClick={() => ouvrirValidationRapport("envoyer")}
          >
            <Icon sx={{ mr: 0.5 }}>playlist_add</Icon> Envoyer au suivi ({nonEnvoyees})
          </MDButton>
        )}
        {statut !== "BROUILLON" && canValiderRapport && (
          <MDButton
            size="small"
            variant="text"
            color="secondary"
            disabled={rapportWorkflowBusy}
            onClick={rouvrirRapport}
          >
            <Icon sx={{ mr: 0.5 }}>undo</Icon>
            {statut === "VALIDE" ? "Annuler la validation" : "Renvoyer en brouillon"}
          </MDButton>
        )}
      </MDBox>
    );
  }

  function renderValidationDialog() {
    const { open, mode, selected } = validationDialog;
    if (!rapportData) return null;
    const candidates = rapportData.lacunes.filter((l) => !l.recommandation_suivi_id);
    const dejaEnvoyees = rapportData.lacunes.length - candidates.length;
    const toggle = (id) =>
      setValidationDialog((prev) => ({
        ...prev,
        selected: prev.selected.includes(id)
          ? prev.selected.filter((x) => x !== id)
          : [...prev.selected, id],
      }));
    return (
      <Dialog
        open={open}
        onClose={() => setValidationDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {mode === "valider" ? "Valider le rapport" : "Envoyer des lacunes au suivi"}
        </DialogTitle>
        <DialogContent dividers>
          {mode === "valider" && (
            <MDTypography variant="body2" sx={{ mb: 1.5 }}>
              Le rapport sera enregistré tel qu&apos;affiché puis figé (visa). Cochez les lacunes à
              transformer en recommandations dans le Suivi des recommandations.
            </MDTypography>
          )}
          {rapportData.nb_recommandations_suivi > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
              Cette mission a déjà {rapportData.nb_recommandations_suivi} recommandation(s) dans le
              Suivi{dejaEnvoyees ? `, dont ${dejaEnvoyees} issue(s) de ce rapport` : ""}. Vérifiez
              qu&apos;elles ne correspondent pas aux lacunes cochées, pour éviter les doublons.
            </Alert>
          )}
          {candidates.length === 0 ? (
            <MDTypography variant="caption" color="text">
              Aucune lacune à envoyer
              {rapportData.lacunes.length ? " (toutes ont déjà été envoyées)" : ""}.
            </MDTypography>
          ) : (
            <>
              <MDBox display="flex" gap={1} mb={1}>
                <MDButton
                  size="small"
                  variant="text"
                  color="info"
                  onClick={() =>
                    setValidationDialog((prev) => ({
                      ...prev,
                      selected: candidates.map((l) => l.id),
                    }))
                  }
                >
                  Tout cocher
                </MDButton>
                <MDButton
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={() => setValidationDialog((prev) => ({ ...prev, selected: [] }))}
                >
                  Tout décocher
                </MDButton>
              </MDBox>
              {candidates.map((l) => (
                <MDBox
                  key={l.id}
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  py={0.75}
                  sx={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                  onClick={() => toggle(l.id)}
                >
                  <Checkbox
                    size="small"
                    checked={selected.includes(l.id)}
                    sx={{ p: 0.25 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(l.id)}
                  />
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ display: "block" }}>
                      N° {l.numero_ordre} · {l.volet_theme} · Priorité {l.priorite}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                      {l.constat || l.critere}
                    </MDTypography>
                    {l.recommandation && (
                      <MDTypography variant="caption" sx={{ display: "block", color: "#0A3475" }}>
                        Recommandation : {l.recommandation}
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setValidationDialog((prev) => ({ ...prev, open: false }))}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            disabled={rapportWorkflowBusy || (mode === "envoyer" && selected.length === 0)}
            onClick={confirmerValidationRapport}
          >
            {rapportWorkflowBusy ? (
              <CircularProgress size={16} color="inherit" />
            ) : mode === "valider" ? (
              `Valider${selected.length ? ` et envoyer ${selected.length} au suivi` : ""}`
            ) : (
              `Envoyer ${selected.length} au suivi`
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  function renderParametresRapportDialog() {
    return (
      <Dialog
        open={parametresDialog.open}
        onClose={() => setParametresDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Paramètres du rapport</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
            Communs à tous les rapports de contrôle de l&apos;institution : imprimés sur la page de
            garde (impression et Word).
          </MDTypography>
          <TextField
            fullWidth
            label="Direction émettrice"
            placeholder="Ex. : Direction Audit-Inspection"
            InputLabelProps={{ shrink: true }}
            value={parametresDialog.direction}
            onChange={(e) =>
              setParametresDialog((prev) => ({ ...prev, direction: e.target.value }))
            }
            inputProps={{ maxLength: 200 }}
            helperText="Laissée vide : « Contrôle & Audit »."
          />
          <MDBox mt={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <MDBox
              sx={{
                width: 140,
                height: 80,
                border: "1px dashed #ccc",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {rapportData?.has_logo && rapportLogoDataUrl ? (
                <img
                  src={rapportLogoDataUrl}
                  alt="Logo"
                  style={{ maxWidth: "100%", maxHeight: "100%" }}
                />
              ) : (
                <MDTypography variant="caption" color="text">
                  Aucun logo
                </MDTypography>
              )}
            </MDBox>
            <MDButton variant="outlined" color="info" size="small" component="label">
              <Icon sx={{ mr: 0.5 }}>upload</Icon> Choisir un logo
              <input
                type="file"
                hidden
                accept=".png,.jpg,.jpeg"
                onChange={(e) => {
                  televerserLogoRapport(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </MDButton>
            {rapportData?.has_logo && (
              <MDButton variant="text" color="error" size="small" onClick={supprimerLogoRapport}>
                Supprimer
              </MDButton>
            )}
          </MDBox>
          <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
            PNG ou JPG, 2 Mo maximum.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setParametresDialog((prev) => ({ ...prev, open: false }))}
          >
            Fermer
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={parametresDialog.saving}
            onClick={enregistrerParametresRapport}
          >
            Enregistrer
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  function renderRapportTab() {
    return (
      <MDBox>
        <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={3}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel shrink>Mission</InputLabel>
            <Select
              label="Mission"
              displayEmpty
              notched
              value={rapportMissionId}
              onChange={(e) => {
                setRapportMissionId(e.target.value);
                setRapportData(null);
              }}
              sx={{ height: "45px" }}
            >
              <MenuItem value="">Sélectionner une mission</MenuItem>
              {rapportMissions.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.reference}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDButton
            variant="gradient"
            color="info"
            disabled={!rapportMissionId || loadingRapport}
            onClick={genererRapport}
          >
            {loadingRapport ? <CircularProgress size={16} color="inherit" /> : "Générer le rapport"}
          </MDButton>
          {rapportData && (
            <>
              <MDButton variant="outlined" color="info" onClick={imprimerRapport}>
                <Icon sx={{ mr: 0.5 }}>print</Icon> Imprimer
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                disabled={exportingDocx}
                onClick={exporterRapportWord}
              >
                {exportingDocx ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <>
                    <Icon sx={{ mr: 0.5 }}>description</Icon> Word
                  </>
                )}
              </MDButton>
              {canCreateMission && (
                <Tooltip title="Direction émettrice et logo de la page de garde">
                  <MDButton variant="text" color="secondary" onClick={ouvrirParametresRapport}>
                    <Icon sx={{ mr: 0.5 }}>settings</Icon> Paramètres
                  </MDButton>
                </Tooltip>
              )}
            </>
          )}
        </MDBox>

        {rapportData &&
          (() => {
            const canEditRapport =
              rapportData.rapport_statut !== "VALIDE" &&
              (canEditResultats ||
                (rapportData.superviseurs || []).some((s) => s.id === user?.profile?.id));
            const nbEnvoyees = rapportData.lacunes.filter((l) => l.recommandation_suivi_id).length;
            return (
              <MDBox sx={{ border: "1px solid #e0e0e0", borderRadius: "10px", p: 3 }}>
                <MDTypography variant="h6" sx={{ color: "#0A3475", mb: 0.5 }}>
                  {rapportData.reference}
                </MDTypography>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                  Chef de mission : {rapportData.chef_mission_nom || "-"} · Contrôleur :{" "}
                  {rapportData.controleur_affecte_nom || "-"}
                  {rapportData.superviseurs.length > 0 &&
                    ` · Superviseurs : ${rapportData.superviseurs.map((s) => s.nom).join(", ")}`}
                </MDTypography>
                {renderRapportWorkflow(canEditRapport)}

                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
                  En-tête
                </MDTypography>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                  Repris dans l&apos;introduction (bouton « Régénérer »).
                </MDTypography>
                <MDBox display="flex" gap={2} flexWrap="wrap" mb={3}>
                  <TextField
                    label="N° de la lettre de mission"
                    placeholder="Ex. : 004000/24/DG/DAI/SI/KP"
                    InputLabelProps={{ shrink: true }}
                    value={rapportSections.lettre_mission_numero || ""}
                    onChange={(e) =>
                      setRapportSections((prev) => ({
                        ...prev,
                        lettre_mission_numero: e.target.value,
                      }))
                    }
                    disabled={!canEditRapport}
                    inputProps={{ maxLength: 100 }}
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                  <TextField
                    label="Date de la lettre de mission"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={rapportSections.lettre_mission_date || ""}
                    onChange={(e) =>
                      setRapportSections((prev) => ({
                        ...prev,
                        lettre_mission_date: e.target.value,
                      }))
                    }
                    disabled={!canEditRapport}
                    sx={{ width: 200 }}
                  />
                  <TextField
                    label="Destinataires du rapport"
                    placeholder="Ex. : Conseil de Surveillance, Conseil d'Administration, Direction Générale"
                    InputLabelProps={{ shrink: true }}
                    value={rapportSections.rapport_destinataires || ""}
                    onChange={(e) =>
                      setRapportSections((prev) => ({
                        ...prev,
                        rapport_destinataires: e.target.value,
                      }))
                    }
                    disabled={!canEditRapport}
                    inputProps={{ maxLength: 2000 }}
                    sx={{ flex: 2, minWidth: 280 }}
                  />
                </MDBox>

                {renderSectionRapport(
                  "Introduction",
                  "rapport_introduction_html",
                  canEditRapport,
                  "Générée à partir de la mission (période, équipe, lettre de mission, volets, " +
                    "destinataires) : complétez au besoin.",
                  () => regenererSection("rapport_introduction_html")
                )}
                {renderSectionRapport(
                  "I. Présentation de l'institution",
                  "rapport_presentation_html",
                  canEditRapport,
                  "Siège, agences, agrément, dirigeants… Laissée vide, cette section n'apparaît " +
                    "pas dans le rapport."
                )}
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{ display: "block", color: "#0A3475", mb: 1.5, textTransform: "uppercase" }}
                >
                  Rapport détaillé
                </MDTypography>
                {rapportData.volets.length === 0 ? (
                  <MDTypography variant="body2" color="text">
                    Aucun volet sur cette mission.
                  </MDTypography>
                ) : (
                  rapportData.volets.map((v) => (
                    <MDBox key={v.mission_volet_id} mb={3}>
                      <MDBox display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
                          {v.volet_code} - {v.volet_theme}
                        </MDTypography>
                        {v.date_cloture && (
                          <>
                            <Chip
                              label={NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.label}
                              size="small"
                              sx={{
                                bgcolor: NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.bg,
                                color: NIVEAU_CONFORMITE_COLORS[v.niveau_conformite]?.color,
                                fontWeight: 700,
                              }}
                            />
                            <Chip
                              label={NIVEAU_RISQUE_COLORS[v.niveau_risque]?.label}
                              size="small"
                              sx={{
                                bgcolor: NIVEAU_RISQUE_COLORS[v.niveau_risque]?.bg,
                                color: NIVEAU_RISQUE_COLORS[v.niveau_risque]?.color,
                                fontWeight: 700,
                              }}
                            />
                            <MDButton
                              size="small"
                              variant="text"
                              color="secondary"
                              onClick={() => toggleHistorique(v.mission_volet_id)}
                              sx={{ fontSize: "0.7rem", py: 0 }}
                            >
                              <Icon sx={{ fontSize: "1rem", mr: 0.3 }}>history</Icon>
                              Historique
                            </MDButton>
                            {canEditRapport && (
                              <Tooltip title="Reprend l'objectif, le résultat attendu, les constats de la feuille de travail et la conclusion du volet">
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="secondary"
                                  onClick={() => regenererVolet(v)}
                                  sx={{ fontSize: "0.7rem", py: 0 }}
                                >
                                  <Icon sx={{ fontSize: "1rem", mr: 0.3 }}>autorenew</Icon>
                                  Régénérer depuis la feuille de travail
                                </MDButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </MDBox>
                      {openHistoriqueVoletId === v.mission_volet_id && (
                        <MDBox
                          mb={1.5}
                          sx={{
                            bgcolor: "#f8f9fa",
                            borderRadius: "8px",
                            p: 1.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          {!rapportHistorique[v.mission_volet_id] ? (
                            <CircularProgress size={14} />
                          ) : rapportHistorique[v.mission_volet_id].length === 0 ? (
                            <MDTypography variant="caption" color="text">
                              Aucune modification enregistrée pour l&apos;instant.
                            </MDTypography>
                          ) : (
                            rapportHistorique[v.mission_volet_id].map((h) => (
                              <MDTypography
                                key={h.id}
                                variant="caption"
                                sx={{ display: "block", color: "#4B5E80" }}
                              >
                                {h.user_nom} - {new Date(h.date_action).toLocaleString("fr-FR")}
                              </MDTypography>
                            ))
                          )}
                        </MDBox>
                      )}
                      {v.date_cloture ? (
                        <>
                          <RichTextEditor
                            key={`${v.mission_volet_id}-${
                              editorVersions[`volet-${v.mission_volet_id}`] || 0
                            }`}
                            value={rapportEditState[v.mission_volet_id]}
                            onChange={(html) =>
                              setRapportEditState((prev) => ({
                                ...prev,
                                [v.mission_volet_id]: html,
                              }))
                            }
                            disabled={!canEditRapport}
                          />
                          {canEditRapport && (
                            <MDBox display="flex" justifyContent="flex-end" mt={1}>
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="info"
                                disabled={savingRapportVoletId === v.mission_volet_id}
                                onClick={() => saveRapportVolet(v.mission_volet_id)}
                              >
                                {savingRapportVoletId === v.mission_volet_id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  "Enregistrer ce chapitre"
                                )}
                              </MDButton>
                            </MDBox>
                          )}
                        </>
                      ) : (
                        <MDTypography
                          variant="caption"
                          sx={{ color: "#c62828", fontStyle: "italic" }}
                        >
                          Volet non clôturé - conclusion non disponible.
                        </MDTypography>
                      )}
                    </MDBox>
                  ))
                )}
                <MDBox mb={3}>
                  <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
                    Synthèse des lacunes et recommandations
                  </MDTypography>
                  <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                    {rapportData.lacunes.length} lacune(s), reprise(s) automatiquement de
                    l&apos;onglet Lacunes de la mission (Lacunes, Risques/conséquences,
                    Recommandations, Échéancier, Responsable de suivi) et regroupée(s) par volet
                    dans le rapport.
                    {nbEnvoyees > 0 &&
                      ` ${nbEnvoyees} déjà envoyée(s) au Suivi des recommandations.`}
                  </MDTypography>
                </MDBox>
                {renderSectionRapport(
                  "Conclusion",
                  "rapport_conclusion_html",
                  canEditRapport,
                  null,
                  () => regenererSection("rapport_conclusion_html")
                )}
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
                  Attestation
                </MDTypography>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                  Signée par le chef de mission ({rapportData.chef_mission_nom || "-"}). Visa
                  imprimé sous la mention « VU » ; à défaut de nom, celui du valideur est repris à
                  la validation.
                </MDTypography>
                <MDBox display="flex" gap={2} flexWrap="wrap">
                  <TextField
                    label="Titre du signataire du visa"
                    placeholder="Ex. : Le Directeur de l'Audit et de l'Inspection"
                    InputLabelProps={{ shrink: true }}
                    value={rapportSections.rapport_visa_titre || ""}
                    onChange={(e) =>
                      setRapportSections((prev) => ({
                        ...prev,
                        rapport_visa_titre: e.target.value,
                      }))
                    }
                    disabled={!canEditRapport}
                    inputProps={{ maxLength: 200 }}
                    sx={{ flex: 1, minWidth: 260 }}
                  />
                  <TextField
                    label="Nom du signataire du visa"
                    InputLabelProps={{ shrink: true }}
                    value={rapportSections.rapport_visa_nom || ""}
                    onChange={(e) =>
                      setRapportSections((prev) => ({ ...prev, rapport_visa_nom: e.target.value }))
                    }
                    disabled={!canEditRapport}
                    inputProps={{ maxLength: 200 }}
                    sx={{ flex: 1, minWidth: 260 }}
                  />
                </MDBox>
                {canEditRapport && (
                  <MDBox display="flex" justifyContent="flex-end" mt={2}>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="info"
                      disabled={savingRapportSections}
                      onClick={() => saveRapportSections()}
                    >
                      {savingRapportSections ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        "Enregistrer en-tête, introduction, présentation, conclusion et attestation"
                      )}
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        {renderValidationDialog()}
        {renderParametresRapportDialog()}
      </MDBox>
    );
  }

  function renderMissionsTab() {
    return (
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <MDBox display="flex" gap={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Année</InputLabel>
              <Select
                label="Année"
                value={filterAnneeMission}
                onChange={(e) => setFilterAnneeMission(e.target.value)}
                sx={{ height: "45px" }}
              >
                {[
                  filterAnneeMission - 1,
                  filterAnneeMission,
                  filterAnneeMission + 1,
                  filterAnneeMission + 2,
                ]
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .sort((a, b) => a - b)
                  .map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </MDBox>
          {canCreateMission && (
            <MDButton variant="gradient" color="info" onClick={openCreateMission}>
              <Icon sx={{ mr: 0.5 }}>add</Icon> Créer une mission
            </MDButton>
          )}
        </MDBox>

        {loadingMissions ? (
          <MDBox display="flex" justifyContent="center" p={5}>
            <CircularProgress color="info" />
          </MDBox>
        ) : missions.length === 0 ? (
          <Card>
            <MDBox p={4} textAlign="center">
              <Icon sx={{ fontSize: 48, color: "grey.400" }}>assignment</Icon>
              <MDTypography color="text" mt={1} variant="h6">
                Aucune mission pour cette sélection.
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <DataTable
            table={{ columns: missionColumns, rows: missions }}
            isSorted
            entriesPerPage
            showTotalEntries
          />
        )}
      </MDBox>
    );
  }

  // ── Onglet F : Tableau de bord (§ 6.7) ──────────────────────────────────
  const navigate = useNavigate();
  const [dashYear, setDashYear] = useState(new Date().getFullYear());
  const [dashData, setDashData] = useState(null);
  const [loadingDash, setLoadingDash] = useState(false);
  const dashVoletsDoughnutRef = useRef(null);
  const dashConformiteBarRef = useRef(null);
  const dashRisqueBarRef = useRef(null);
  const dashRecoDoughnutRef = useRef(null);
  const dashNatureBarRef = useRef(null);
  const dashTitreBarRef = useRef(null);
  const dashEvolutionLineRef = useRef(null);

  useEffect(() => {
    if (activeTab !== 4) return;
    setLoadingDash(true);
    const params = new URLSearchParams({ annee: dashYear });
    if (institutionParam) params.append("institution_id", institutionParam);
    api
      .get(`/api/controle/tableau-de-bord?${params}`)
      .then((r) => setDashData(r.data))
      .catch(() => showSnack("Erreur lors du chargement du tableau de bord.", "error"))
      .finally(() => setLoadingDash(false));
  }, [activeTab, dashYear, institutionParam, showSnack]);

  function imprimerTableauDeBord() {
    if (!dashData) return;
    const { plan, conformite, recommandations: rec } = dashData;
    const chartImg = (ref, title) => {
      const img = ref.current?.toBase64Image?.();
      return img
        ? `<div class="chart-box"><div class="chart-title">${escHtml(
            title
          )}</div><img src="${img}"></div>`
        : "";
    };
    const voletRows = conformite.par_volet
      .map(
        (v) => `<tr><td><strong>${escHtml(v.code)}</strong> ${escHtml(v.theme)}</td>
      <td style="text-align:center">${v.exigences_evaluees}</td>
      <td style="text-align:center">${v.c}</td><td style="text-align:center">${v.pc}</td>
      <td style="text-align:center">${v.nc}</td>
      <td style="text-align:center">${v.risque_faible} / ${v.risque_moyen} / ${
          v.risque_eleve
        }</td></tr>`
      )
      .join("");
    const natureRows = rec.par_nature
      .map(
        (n) => `<tr><td>${escHtml(
          NATURE_LABELS[n.nature] || n.nature
        )}</td><td style="text-align:center">${n.total}</td>
      <td style="text-align:center">${n.te_pct}%</td><td style="text-align:center">${n.pe_pct}%</td>
      <td style="text-align:center">${n.ne_pct}%</td></tr>`
      )
      .join("");
    const cbFirst = [...rec.par_titre].sort(
      (a, b) =>
        (b.nature === "COMMISSION_BANCAIRE") - (a.nature === "COMMISSION_BANCAIRE") ||
        a.titre.localeCompare(b.titre)
    );
    const titreRows = cbFirst
      .map(
        (t) => `<tr><td>${escHtml(t.titre)}</td><td>${escHtml(
          NATURE_LABELS[t.nature] || t.nature
        )}</td><td style="text-align:center">${t.total}</td>
      <td style="text-align:center">${t.te_pct}%</td><td style="text-align:center">${t.pe_pct}%</td>
      <td style="text-align:center">${t.ne_pct}%</td></tr>`
      )
      .join("");
    const bodyHtml = `
      <h2>Plan annuel ${dashData.annee}</h2>
      <table><thead><tr><th>Lignes planifiées</th><th>Réalisées</th><th>En cours</th>
      <th>En retard</th><th>Taux de réalisation</th></tr></thead>
      <tbody><tr><td>${plan.total}</td><td>${plan.realise}</td><td>${plan.en_cours}</td>
      <td>${plan.en_retard}</td><td>${plan.taux_realisation}%</td></tr></tbody></table>

      <h2>Conformité et risque par volet (${conformite.volets_evalues}/${
      conformite.volets_total
    } volets évalués - ${conformite.lacunes_total} lacune(s) dont ${
      conformite.lacunes_haute
    } de priorité haute)</h2>
      <div style="display:flex;gap:14px;flex-wrap:wrap">
        ${chartImg(dashVoletsDoughnutRef, "Volets évalués")}
        ${chartImg(dashConformiteBarRef, "Conformité par volet")}
      </div>
      ${chartImg(dashRisqueBarRef, "Risque par volet")}
      <table><thead><tr><th>Volet</th><th>Exigences évaluées</th><th>C</th><th>PC</th><th>NC</th>
      <th>Risque F / M / É</th></tr></thead>
      <tbody>${voletRows || '<tr><td colspan="6">Aucun volet évalué.</td></tr>'}</tbody></table>

      <h2>Suivi des recommandations (TE ${rec.te_pct}% · PE ${rec.pe_pct}% · NE ${rec.ne_pct}%)</h2>
      <div style="display:flex;gap:14px;flex-wrap:wrap">
        ${chartImg(dashRecoDoughnutRef, "Taux d'exécution global")}
        ${chartImg(dashNatureBarRef, "Par nature de mission")}
      </div>
      <table><thead><tr><th>Nature</th><th>Total</th><th>TE</th><th>PE</th><th>NE</th></tr></thead>
      <tbody>${natureRows || '<tr><td colspan="5">Aucune recommandation.</td></tr>'}</tbody></table>

      ${
        cbFirst.length > 0
          ? `<h2>Taux d'exécution par titre (Commission bancaire en premier)</h2>
      ${chartImg(dashTitreBarRef, "Par titre")}
      <table><thead><tr><th>Titre</th><th>Nature</th><th>Total</th><th>TE</th><th>PE</th><th>NE</th></tr></thead>
      <tbody>${titreRows}</tbody></table>`
          : ""
      }

      ${
        rec.evolution && rec.evolution.length > 0
          ? `<h2>Évolution du taux d'exécution par nature de mission</h2>
      ${chartImg(dashEvolutionLineRef, "Évolution mensuelle")}`
          : ""
      }`;
    const kpiHtml =
      kpiTile(`${conformite.volets_evalues} / ${conformite.volets_total}`, "Volets évalués") +
      kpiTile(conformite.lacunes_total, "Lacunes", conformite.lacunes_haute > 0) +
      kpiTile(rec.total, "Recommandations") +
      kpiTile(`${rec.te_pct}%`, "Taux d'exécution (TE)") +
      kpiTile(rec.non_executees, "Non exécutées", rec.non_executees > 0);
    const html = buildPrintShell({
      title: `Tableau de bord ${dashData.annee}`,
      eyebrow: "ConformIMF · Contrôle & Audit",
      heading: `Tableau de bord ${dashData.annee}`,
      subtitle: `${conformite.lacunes_total} lacune(s) · ${rec.non_executees} recommandation(s) non exécutée(s)`,
      kpiHtml,
      bodyHtml,
    });
    openPrintWindow(html, () =>
      showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning")
    );
  }

  function renderTableauDeBord() {
    if (loadingDash || !dashData) {
      return (
        <MDBox display="flex" justifyContent="center" p={5}>
          <CircularProgress color="info" />
        </MDBox>
      );
    }
    const { plan, conformite, recommandations: rec } = dashData;
    const tiles = [
      { label: "Lignes planifiées", value: plan.total, icon: "list_alt", color: "#0A3475" },
      { label: "Réalisées", value: plan.realise, icon: "check_circle", color: "#2e7d32" },
      { label: "En cours", value: plan.en_cours, icon: "hourglass_top", color: "#e65100" },
      { label: "En retard", value: plan.en_retard, icon: "warning", color: "#c62828" },
      {
        label: "Taux de réalisation",
        value: `${plan.taux_realisation}%`,
        icon: "insights",
        color: "#186A81",
      },
    ];
    const sectionTitle = (text) => (
      <MDTypography variant="h6" sx={{ color: "#0A3475", mt: 3, mb: 1.5 }}>
        {text}
      </MDTypography>
    );
    const cbFirst = [...rec.par_titre].sort(
      (a, b) =>
        (b.nature === "COMMISSION_BANCAIRE") - (a.nature === "COMMISSION_BANCAIRE") ||
        a.titre.localeCompare(b.titre)
    );
    const etatDatasets = (items, key) => [
      {
        label: "Totalement exécuté",
        data: items.map((x) => x.te),
        backgroundColor: "#2e7d32",
      },
      {
        label: "Partiellement exécuté",
        data: items.map((x) => x.pe),
        backgroundColor: "#e65100",
      },
      { label: "Non exécuté", data: items.map((x) => x.ne), backgroundColor: "#c62828" },
      {
        label: "Non renseigné",
        data: items.map((x) => x.non_renseigne),
        backgroundColor: "#bdbdbd",
      },
    ];
    const stackedBarOptions = {
      responsive: true,
      plugins: { legend: { position: "top" } },
      scales: { x: { stacked: true }, y: { stacked: true, ticks: { stepSize: 1 } } },
    };
    const EVOLUTION_PALETTE = [
      "#0A3475",
      "#186A81",
      "#c62828",
      "#e65100",
      "#6a1b9a",
      "#2e7d32",
      "#455a64",
      "#ad1457",
    ];
    const buildEvolutionChart = (evolution) => {
      const moisSet = [...new Set(evolution.map((e) => e.mois))].sort((a, b) => a - b);
      const natures = [...new Set(evolution.map((e) => e.nature))];
      return {
        labels: moisSet.map((m) => MOIS[m - 1].slice(0, 4)),
        datasets: natures.map((n, i) => ({
          label: NATURE_LABELS[n] || n,
          data: moisSet.map((m) => {
            const row = evolution.find((e) => e.nature === n && e.mois === m);
            return row ? row.taux_execution : null;
          }),
          borderColor: EVOLUTION_PALETTE[i % EVOLUTION_PALETTE.length],
          backgroundColor: EVOLUTION_PALETTE[i % EVOLUTION_PALETTE.length],
          spanGaps: true,
          tension: 0.3,
        })),
      };
    };
    const evolutionOptions = {
      responsive: true,
      plugins: { legend: { position: "bottom" } },
      scales: { y: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } } },
    };

    return (
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Année</InputLabel>
            <Select
              label="Année"
              value={dashYear}
              onChange={(e) => setDashYear(e.target.value)}
              sx={{ height: "45px" }}
            >
              {[dashYear - 1, dashYear, dashYear + 1]
                .filter((v, i, arr) => arr.indexOf(v) === i)
                .map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <MDButton size="small" variant="outlined" color="info" onClick={imprimerTableauDeBord}>
            <Icon sx={{ mr: 0.5 }}>print</Icon> Imprimer
          </MDButton>
        </MDBox>

        {sectionTitle("Plan annuel")}
        <Grid container spacing={2}>
          {tiles.map((tl) => (
            <Grid item xs={6} md={2.4} key={tl.label}>
              <Card sx={{ p: 2, textAlign: "center", height: "100%" }}>
                <Icon sx={{ color: tl.color, fontSize: "1.6rem !important" }}>{tl.icon}</Icon>
                <MDTypography variant="h4" sx={{ color: tl.color, fontWeight: 700, mt: 0.5 }}>
                  {tl.value}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {tl.label}
                </MDTypography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {sectionTitle("Missions et conformité par volet")}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Volets évalués
              </MDTypography>
              {conformite.volets_total === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="caption" color="text">
                    Aucun volet dans le référentiel.
                  </MDTypography>
                </MDBox>
              ) : (
                <Doughnut
                  ref={dashVoletsDoughnutRef}
                  data={{
                    labels: ["Évalués", "Restants"],
                    datasets: [
                      {
                        data: [
                          conformite.volets_evalues,
                          Math.max(conformite.volets_total - conformite.volets_evalues, 0),
                        ],
                        backgroundColor: ["#186A81", "#e0e0e0"],
                        borderWidth: 2,
                        borderColor: "#fff",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => ` ${ctx.label} : ${ctx.parsed}`,
                        },
                      },
                    },
                  }}
                />
              )}
              <MDTypography
                variant="caption"
                color="text"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                {conformite.volets_evalues} / {conformite.volets_total} volets du programme
              </MDTypography>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Conformité par volet
              </MDTypography>
              {conformite.par_volet.length === 0 ? (
                <MDBox p={3} textAlign="center">
                  <Icon sx={{ fontSize: 48, color: "grey.400" }}>bar_chart</Icon>
                  <MDTypography color="text" mt={1} variant="h6">
                    Aucun volet clôturé pour {dashData.annee}.
                  </MDTypography>
                </MDBox>
              ) : (
                <Bar
                  ref={dashConformiteBarRef}
                  data={{
                    labels: conformite.par_volet.map((v) => v.code),
                    datasets: [
                      {
                        label: "Conforme",
                        data: conformite.par_volet.map((v) => v.c),
                        backgroundColor: "#2e7d32",
                      },
                      {
                        label: "Partiellement conforme",
                        data: conformite.par_volet.map((v) => v.pc),
                        backgroundColor: "#e65100",
                      },
                      {
                        label: "Non conforme",
                        data: conformite.par_volet.map((v) => v.nc),
                        backgroundColor: "#c62828",
                      },
                    ],
                  }}
                  options={stackedBarOptions}
                />
              )}
            </Card>
          </Grid>

          {conformite.par_volet.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                  Risque par volet
                </MDTypography>
                <Bar
                  ref={dashRisqueBarRef}
                  data={{
                    labels: conformite.par_volet.map((v) => v.code),
                    datasets: [
                      {
                        label: "Faible",
                        data: conformite.par_volet.map((v) => v.risque_faible),
                        backgroundColor: "#2e7d32",
                      },
                      {
                        label: "Moyen",
                        data: conformite.par_volet.map((v) => v.risque_moyen),
                        backgroundColor: "#e65100",
                      },
                      {
                        label: "Élevé",
                        data: conformite.par_volet.map((v) => v.risque_eleve),
                        backgroundColor: "#c62828",
                      },
                    ],
                  }}
                  options={stackedBarOptions}
                />
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Chip
                icon={<Icon sx={{ fontSize: "0.85rem !important" }}>report_problem</Icon>}
                label={`${conformite.lacunes_total} lacune(s) dont ${conformite.lacunes_haute} de priorité haute`}
                size="small"
                sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }}
              />
              {canSeeMissionsTabs && (
                <MDButton size="small" variant="text" color="info" onClick={() => setActiveTab(2)}>
                  Voir les missions et lacunes
                </MDButton>
              )}
            </MDBox>
          </Grid>
        </Grid>

        {sectionTitle("Suivi des recommandations")}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Taux d&apos;exécution global
              </MDTypography>
              {rec.total === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="caption" color="text">
                    Aucune recommandation.
                  </MDTypography>
                </MDBox>
              ) : (
                <Doughnut
                  ref={dashRecoDoughnutRef}
                  data={{
                    labels: [
                      "Totalement exécuté",
                      "Partiellement exécuté",
                      "Non exécuté",
                      "Non renseigné",
                    ],
                    datasets: [
                      {
                        data: [rec.te, rec.pe, rec.ne, rec.non_renseigne],
                        backgroundColor: ["#2e7d32", "#e65100", "#c62828", "#bdbdbd"],
                        borderWidth: 2,
                        borderColor: "#fff",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => ` ${ctx.label} : ${ctx.parsed}`,
                        },
                      },
                    },
                  }}
                />
              )}
              <MDTypography
                variant="caption"
                color="text"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                {rec.total} recommandation(s) · TE {rec.te_pct}%
              </MDTypography>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Par nature de mission
              </MDTypography>
              {rec.par_nature.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="caption" color="text">
                    Aucune donnée disponible.
                  </MDTypography>
                </MDBox>
              ) : (
                <Bar
                  ref={dashNatureBarRef}
                  data={{
                    labels: rec.par_nature.map((n) => NATURE_LABELS[n.nature] || n.nature),
                    datasets: etatDatasets(rec.par_nature),
                  }}
                  options={stackedBarOptions}
                />
              )}
            </Card>
          </Grid>

          {cbFirst.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                  Taux d&apos;exécution par titre (Commission bancaire en premier)
                </MDTypography>
                <Bar
                  ref={dashTitreBarRef}
                  data={{
                    labels: cbFirst.map((tt) => tt.titre),
                    datasets: etatDatasets(cbFirst),
                  }}
                  options={stackedBarOptions}
                />
              </Card>
            </Grid>
          )}

          {rec.evolution && rec.evolution.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 2 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                  Évolution du taux d&apos;exécution par nature de mission
                </MDTypography>
                <Line
                  ref={dashEvolutionLineRef}
                  data={buildEvolutionChart(rec.evolution)}
                  options={evolutionOptions}
                />
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", textAlign: "center", mt: 1 }}
                >
                  Instantané pris chaque mois lors de la consultation du tableau de bord —
                  l&apos;historique se construit progressivement à partir de ce mois-ci.
                </MDTypography>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              onClick={() => navigate("/suivi-recommandations?etat=NON_TE")}
            >
              <Icon sx={{ mr: 0.5 }}>track_changes</Icon> Voir les {rec.non_executees}{" "}
              recommandation(s) non exécutée(s)
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  }

  // ── Rendu par onglet ────────────────────────────────────────────────────
  function renderTabContent() {
    switch (activeTab) {
      case 0:
        return renderReferentielTab();
      case 1:
        return renderPlanAnnuelTab();
      case 2:
        if (loadingMissionDetail) {
          return (
            <MDBox display="flex" justifyContent="center" p={5}>
              <CircularProgress color="info" />
            </MDBox>
          );
        }
        if (openVolet) return renderFeuilleTravail();
        if (missionDetail) return renderMissionDetail();
        return renderMissionsTab();
      case 3:
        return renderRapportTab();
      case 4:
        return renderTableauDeBord();
      default:
        return null;
    }
  }

  // ── Rendu principal ─────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="purple"
            borderRadius="lg"
            coloredShadow="dark"
            sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
          >
            <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
              Contrôle & Audit
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
            >
              Contrôle permanent, conformité et audit LBC/FT/FP
            </MDTypography>
          </MDBox>

          <MDBox px={2} pt={2} pb={2}>
            {isAdminGlobal && (
              <MDBox
                mb={2}
                sx={{
                  background: "linear-gradient(135deg, #eef2f8 0%, #e8f0fb 100%)",
                  border: "1px solid #c8d8ee",
                  borderLeft: "4px solid #0A3475",
                  borderRadius: "10px",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "#0A3475", fontSize: "1.3rem" }}>admin_panel_settings</Icon>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: "#0A3475", display: "block", lineHeight: 1.2 }}
                    >
                      Vue Globale
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#7a8eab", fontSize: "0.68rem" }}>
                      Filtrez le plan annuel par institution
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox display="flex" gap={1.5} ml="auto" flexWrap="wrap" alignItems="center">
                  <FormControl sx={{ minWidth: 230 }}>
                    <Select
                      displayEmpty
                      value={selectedInstitution || ""}
                      onChange={(e) => setSelectedInstitution(e.target.value)}
                      sx={{ height: "45px" }}
                      renderValue={(v) =>
                        v ? (
                          institutions.find((i) => i.id === Number(v))?.nom
                        ) : (
                          <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                            Sélectionner une institution
                          </span>
                        )
                      }
                    >
                      {institutions.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                          {inst.nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>
              </MDBox>
            )}

            <MDBox
              display="flex"
              flexWrap="wrap"
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "10px",
                overflow: "hidden",
                backgroundColor: "#f8f9fa",
                width: "fit-content",
              }}
            >
              {TAB_LABELS.map(
                (t, i) =>
                  (canSeeMissionsTabs || i === 4 || (i === 3 && canSeeRapportTab)) && (
                    <div
                      key={i}
                      onClick={() => setActiveTab(i)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        cursor: "pointer",
                        background:
                          activeTab === i
                            ? "linear-gradient(195deg, #0A3475, #186A81)"
                            : "transparent",
                        color: activeTab === i ? "#FFD740" : "#555555",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s",
                        userSelect: "none",
                      }}
                      onMouseEnter={(e) => {
                        if (activeTab !== i) e.currentTarget.style.backgroundColor = "#e8edf5";
                      }}
                      onMouseLeave={(e) => {
                        if (activeTab !== i) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <span>{t.label}</span>
                    </div>
                  )
              )}
            </MDBox>
          </MDBox>
        </Card>

        <Card sx={{ mt: 3 }}>
          <MDBox p={3}>{renderTabContent()}</MDBox>
        </Card>
      </MDBox>

      {/* Dialog Volet */}
      <Dialog open={dialogVolet} onClose={() => setDialogVolet(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingVolet ? "Modifier le volet" : "Nouveau volet de contrôle"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              fullWidth
              size="small"
              label="Code *"
              value={formVolet.code}
              onChange={(e) => setFormVolet({ ...formVolet, code: e.target.value })}
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Thème *"
              value={formVolet.theme}
              onChange={(e) => setFormVolet({ ...formVolet, theme: e.target.value })}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Objectif"
              value={formVolet.objectif}
              onChange={(e) => setFormVolet({ ...formVolet, objectif: e.target.value })}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              size="small"
              label="Résultat attendu"
              value={formVolet.resultat_attendu}
              onChange={(e) => setFormVolet({ ...formVolet, resultat_attendu: e.target.value })}
              multiline
              rows={2}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Domaine</InputLabel>
              <Select
                label="Domaine"
                value={formVolet.domaine_id}
                onChange={(e) => setFormVolet({ ...formVolet, domaine_id: e.target.value })}
              >
                <MenuItem value="">Aucun</MenuItem>
                {domaines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Ordre d'affichage"
              type="number"
              value={formVolet.ordre}
              onChange={(e) => setFormVolet({ ...formVolet, ordre: e.target.value })}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogVolet(false)}
            disabled={savingVolet}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveVolet}
            disabled={savingVolet}
          >
            {savingVolet ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Point de contrôle */}
      <Dialog open={dialogPoint} onClose={() => setDialogPoint(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingPoint ? "Modifier le point de contrôle" : "Nouveau point de contrôle"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              fullWidth
              size="small"
              label="Code *"
              value={formPoint.code}
              onChange={(e) => setFormPoint({ ...formPoint, code: e.target.value })}
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Exigence *"
              value={formPoint.exigence}
              onChange={(e) => setFormPoint({ ...formPoint, exigence: e.target.value })}
              multiline
              rows={2}
              inputProps={{ maxLength: 500 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Poids</InputLabel>
              <Select
                label="Poids"
                value={formPoint.poids}
                onChange={(e) => setFormPoint({ ...formPoint, poids: e.target.value })}
              >
                <MenuItem value={1}>1 - Standard</MenuItem>
                <MenuItem value={2}>2 - Important</MenuItem>
                <MenuItem value={3}>3 - Critique</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Source"
              value={formPoint.source}
              onChange={(e) => setFormPoint({ ...formPoint, source: e.target.value })}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Point de contrôle"
              value={formPoint.point_controle}
              onChange={(e) => setFormPoint({ ...formPoint, point_controle: e.target.value })}
              inputProps={{ maxLength: 300 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Test d'audit"
              value={formPoint.test_audit}
              onChange={(e) => setFormPoint({ ...formPoint, test_audit: e.target.value })}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              size="small"
              label="Preuve attendue"
              value={formPoint.preuve_attendue}
              onChange={(e) => setFormPoint({ ...formPoint, preuve_attendue: e.target.value })}
              multiline
              rows={2}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogPoint(false)}
            disabled={savingPoint}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={savePoint}
            disabled={savingPoint}
          >
            {savingPoint ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Plan annuel */}
      <Dialog open={dialogPlan} onClose={() => setDialogPlan(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingPlan ? "Modifier la ligne de plan" : `Nouvelle ligne - Plan ${selectedYear}`}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth size="small" required disabled={!!editingPlan}>
              <InputLabel>Volet</InputLabel>
              <Select
                label="Volet"
                value={formPlan.volet_id}
                onChange={(e) => setFormPlan({ ...formPlan, volet_id: e.target.value })}
              >
                {volets.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.code} - {v.theme}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Périmètre"
              value={formPlan.perimetre}
              onChange={(e) => setFormPlan({ ...formPlan, perimetre: e.target.value })}
              inputProps={{ maxLength: 500 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Fréquence</InputLabel>
              <Select
                label="Fréquence"
                value={formPlan.frequence}
                onChange={(e) => setFormPlan({ ...formPlan, frequence: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Annuelle">Annuelle</MenuItem>
                <MenuItem value="Semestrielle">Semestrielle</MenuItem>
                <MenuItem value="Trimestrielle">Trimestrielle</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Priorité</InputLabel>
              <Select
                label="Priorité"
                value={formPlan.priorite}
                onChange={(e) => setFormPlan({ ...formPlan, priorite: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Haute">Haute</MenuItem>
                <MenuItem value="Moyenne">Moyenne</MenuItem>
                <MenuItem value="Faible">Faible</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Ressource assignée</InputLabel>
              <Select
                label="Ressource assignée"
                value={formPlan.ressource_assignee_id}
                disabled={isControleurOnly}
                onChange={(e) =>
                  setFormPlan({ ...formPlan, ressource_assignee_id: e.target.value })
                }
              >
                <MenuItem value="">Non assignée</MenuItem>
                {utilisateursInstitution.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Période prévue</InputLabel>
              <Select
                label="Période prévue"
                value={formPlan.periode_prevue}
                onChange={(e) => setFormPlan({ ...formPlan, periode_prevue: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                {MOIS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Durée estimée (jours)"
              type="number"
              value={formPlan.duree_estimee_jours}
              onChange={(e) => setFormPlan({ ...formPlan, duree_estimee_jours: e.target.value })}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogPlan(false)}
            disabled={savingPlan}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={savePlan}
            disabled={savingPlan}
          >
            {savingPlan ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Mission */}
      <Dialog open={dialogMission} onClose={() => setDialogMission(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingMission
              ? `Modifier la mission ${editingMission.reference}`
              : "Créer une mission de contrôle interne"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              fullWidth
              size="small"
              label="Année d'intervention"
              type="number"
              value={formMission.annee_intervention}
              onChange={(e) =>
                setFormMission({ ...formMission, annee_intervention: e.target.value })
              }
              required
              disabled={!!editingMission}
            />

            {!editingMission && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Ligne de plan annuel (optionnel)</InputLabel>
                  <Select
                    label="Ligne de plan annuel (optionnel)"
                    value={formMission.plan_controle_annuel_id}
                    onChange={(e) => applyLignePlan(e.target.value)}
                  >
                    <MenuItem value="">Aucune</MenuItem>
                    {lignesPlanDisponibles.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.volet_code} - {p.volet_theme}
                        {p.ressource_assignee_nom ? ` (${p.ressource_assignee_nom})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Volets à couvrir</InputLabel>
                  <Select
                    multiple
                    label="Volets à couvrir"
                    value={formMission.volet_ids}
                    onChange={(e) => setFormMission({ ...formMission, volet_ids: e.target.value })}
                    renderValue={(selected) =>
                      selected.map((id) => volets.find((v) => v.id === id)?.code || id).join(", ")
                    }
                  >
                    {volets.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.code} - {v.theme}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Chef de mission</InputLabel>
              <Select
                label="Chef de mission"
                value={formMission.chef_mission_id}
                onChange={(e) =>
                  setFormMission({ ...formMission, chef_mission_id: e.target.value })
                }
              >
                <MenuItem value="">Non désigné</MenuItem>
                {utilisateursInstitution.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Contrôleur affecté</InputLabel>
              <Select
                label="Contrôleur affecté"
                value={formMission.controleur_affecte_id}
                onChange={(e) =>
                  setFormMission({ ...formMission, controleur_affecte_id: e.target.value })
                }
              >
                <MenuItem value="">Non affecté</MenuItem>
                {utilisateursInstitution.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Date de début"
              type="date"
              value={formMission.date_debut}
              onChange={(e) => setFormMission({ ...formMission, date_debut: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              label="Date de fin"
              type="date"
              value={formMission.date_fin}
              onChange={(e) => setFormMission({ ...formMission, date_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            {editingMission && (
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  label="Statut"
                  value={formMission.statut}
                  onChange={(e) => setFormMission({ ...formMission, statut: e.target.value })}
                >
                  <MenuItem value="PLANIFIEE">Planifiée</MenuItem>
                  <MenuItem value="EN_COURS">En cours</MenuItem>
                  <MenuItem value="CLOTUREE">Clôturée</MenuItem>
                </Select>
              </FormControl>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogMission(false)}
            disabled={savingMission}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveMission}
            disabled={savingMission}
          >
            {savingMission ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Superviseurs */}
      <Dialog
        open={dialogSuperviseurs}
        onClose={() => setDialogSuperviseurs(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            Superviseurs de la mission
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
            Les superviseurs disposent d&apos;un accès en écriture au rapport de contrôle de cette
            mission, en plus du chef de mission et du contrôleur affecté.
          </MDTypography>
          <FormControl fullWidth size="small">
            <InputLabel>Superviseurs</InputLabel>
            <Select
              multiple
              label="Superviseurs"
              value={formSuperviseurIds}
              onChange={(e) => setFormSuperviseurIds(e.target.value)}
              renderValue={(selected) =>
                selected
                  .map((id) => utilisateursInstitution.find((u) => u.id === id)?.nom)
                  .filter(Boolean)
                  .join(", ")
              }
            >
              {utilisateursInstitution.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogSuperviseurs(false)}
            disabled={savingSuperviseurs}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveSuperviseurs}
            disabled={savingSuperviseurs}
          >
            {savingSuperviseurs ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Clôture de volet */}
      <Dialog open={dialogCloture} onClose={() => setDialogCloture(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            Clôturer le volet
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          {cloturePreview && (
            <MDBox
              sx={{
                background: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 1.5,
                mb: 2,
              }}
            >
              <MDTypography
                variant="caption"
                fontWeight="bold"
                sx={{ color: "#0A3475", display: "block", mb: 1 }}
              >
                Aperçu (calculé à partir des résultats déjà saisis)
              </MDTypography>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <Chip label={`Taux de conformité : ${cloturePreview.taux}%`} size="small" />
                <Chip
                  label={NIVEAU_CONFORMITE_COLORS[cloturePreview.niveauConformite]?.label}
                  size="small"
                  sx={{
                    bgcolor: NIVEAU_CONFORMITE_COLORS[cloturePreview.niveauConformite]?.bg,
                    color: NIVEAU_CONFORMITE_COLORS[cloturePreview.niveauConformite]?.color,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  label={`Risque : ${NIVEAU_RISQUE_COLORS[cloturePreview.niveauRisque]?.label}`}
                  size="small"
                  sx={{
                    bgcolor: NIVEAU_RISQUE_COLORS[cloturePreview.niveauRisque]?.bg,
                    color: NIVEAU_RISQUE_COLORS[cloturePreview.niveauRisque]?.color,
                    fontWeight: 700,
                  }}
                />
              </MDBox>
            </MDBox>
          )}
          <MDTypography variant="caption" sx={{ color: "#5c6b7f", display: "block", mb: 1 }}>
            Brouillon généré à partir des constats et recommandations déjà saisis - à corriger et
            compléter avant de valider.
          </MDTypography>
          <TextField
            fullWidth
            size="small"
            label="Commentaire de conclusion *"
            multiline
            rows={6}
            value={commentaireCloture}
            onChange={(e) => setCommentaireCloture(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogCloture(false)}
            disabled={savingCloture}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            size="small"
            onClick={saveCloture}
            disabled={savingCloture || !commentaireCloture.trim()}
          >
            {savingCloture ? <CircularProgress size={16} color="inherit" /> : "Clôturer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Lacune */}
      <Dialog open={dialogLacune} onClose={() => setDialogLacune(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            Lacune {editingLacune?.numero_ordre} - {editingLacune?.critere}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Cause</InputLabel>
              <Select
                label="Cause"
                value={formLacune.cause}
                onChange={(e) => setFormLacune({ ...formLacune, cause: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                {CAUSE_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Conséquences</InputLabel>
              <Select
                multiple
                label="Conséquences"
                value={formLacune.consequences}
                onChange={(e) => setFormLacune({ ...formLacune, consequences: e.target.value })}
                renderValue={(selected) => selected.join(", ")}
              >
                {CONSEQUENCE_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Recommandation"
              multiline
              rows={3}
              value={formLacune.recommandation}
              onChange={(e) => setFormLacune({ ...formLacune, recommandation: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Délai"
              value={formLacune.delai}
              onChange={(e) => setFormLacune({ ...formLacune, delai: e.target.value })}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Responsable"
              value={formLacune.responsable}
              onChange={(e) => setFormLacune({ ...formLacune, responsable: e.target.value })}
              inputProps={{ maxLength: 200 }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogLacune(false)}
            disabled={savingLacune}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveLacune}
            disabled={savingLacune}
          >
            {savingLacune ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}
